
import React, { useEffect, useMemo, useRef, useState,useCallback } from "react";
import { useLocation } from "react-router-dom";
import { UIInput, UIReactSelect } from "./WarehouseInputfields";
import { buildLegacyUrl } from "components/Tableimageshelpers";
import { Table, flexRender } from "@tanstack/react-table";
import { Package } from "lucide-react";
import { withIconStyle, iconLabelProps } from "components/ViewIcons";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import { AsyncPaginate, type LoadOptions } from "react-select-async-paginate";
import type { GroupBase } from "react-select";
import { getAuthCookie } from "../utils/authCookies";
type SelectOption = { value: string; label: string };
type AdditionalType = { page?: number };
type PreviewFile = { file: File; url: string };
type UploadedImage = { alt_text: string; url: string };
type MobileCardIds = { idColId: string; titleColId: string; actionColId?: string };

type Select2Option = { value: string; label: string };

const PAGE_SIZE_LOCATION = 20;

const locationSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    minHeight: "38px",
    border: state.isFocused ? "1px solid #86b7fe" : "1px solid #ced4da",
    borderRadius: "0.375rem",
    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
    "&:hover": { borderColor: "#86b7fe" },
  }),
  placeholder: (provided: any) => ({ ...provided, color: "#6c757d" }),
  menu: (provided: any) => ({ ...provided, zIndex: 9999 }),
};

type AgentOption = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

type OfficeOption = {
  value: string;
  label: string;
};

type Props = {
  title: string;
  formik: any;
  brandOptions: SelectOption[];
  categoryOptions: SelectOption[];
  /** When provided, brand/category use AsyncPaginate (Select2) instead of UIReactSelect */
  loadBrandOptions?: LoadOptions<SelectOption, GroupBase<SelectOption>, AdditionalType>;
  loadCategoryOptions?: LoadOptions<SelectOption, GroupBase<SelectOption>, AdditionalType>;
  selectedBrandOption?: SelectOption | null;
  selectedCategoryOption?: SelectOption | null;
  selectedBrand?: string;
  setSelectedBrand?: (id: string) => void;
  setSelectedCategories?: (ids: string[]) => void;
  conditionOptions: SelectOption[];
  statusOptions: SelectOption[];
  saving?: boolean;
  onBack: () => void;
  onSubmit: () => void;
  previewFiles: PreviewFile[];
  uploadedFiles: UploadedImage[];
  onAddFiles: (files: File[]) => void;
  onRemovePreview: (idx: number) => void;
  onRemoveUploaded: (idx: number) => void;
  baseUrl?: string;
  warehouseTable?: Table<any>;
  mobileCardIds?: MobileCardIds;
  readOnly?: boolean;
  hideWarehouseTable?: boolean;
  hideSaveButton?: boolean;
  disableQuantityField?: boolean;
  /** When false, Brand / Category are optional (no asterisk). Default true. */
  requireBrandCategorySku?: boolean;
  /** When false, SKU is optional. Defaults to requireBrandCategorySku when omitted. */
  requireSku?: boolean;
  resolveSrc?: (url: string) => string | null;
  onQuantityChange?: (value: string) => void;

  // NEW: tell parent the exact locations chosen so it can build storage_locations
  onLocationsChange?: (
    locs: {
      quantity: number;
      warehouse_id: string; warehouse_name: string;
      zone_id: string;       zone_name: string;
      rack_id: string;       rack_name: string;
      bin_id: string;        bin_name: string;
    }[],
    totalAllocated: number
  ) => void;
  
  // NEW: initial storage locations data from API
  initialStorageLocations?: Array<{
    quantity: number;
    location: Array<{
      quantity: number;
      warehouse_id: string;
      warehouse_name?: string;
      zone_id: string;
      zone_name?: string;
      rack_id: string;
      rack_name?: string;
      bin_id: string;
      bin_name?: string;
      max_volume?: number;  // ✅ Add volume fields
      used_volume?: number;  // ✅ Add volume fields
    }>;
  }>;
  
  // NEW: Agent selection for brokerage agents
  showAgentDropdown?: boolean;
  loadAgentOptions?: any;
  selectedAgentOption?: AgentOption | null;
  onAgentChange?: (agent: AgentOption | null) => void;
  
  // NEW: Office selection for brokerage offices
  showOfficeDropdown?: boolean;
  officeOptions?: OfficeOption[];
  selectedOfficeOption?: OfficeOption | null;
  onOfficeChange?: (office: OfficeOption | null) => void;
  
  // NEW: Type to determine if add or edit mode
  type?: "add" | "edit";

  /**
   * Owner node for warehouse Select2 (signage userId, or parent signage id for teammember).
   * When omitted, resolved from cookie / parent-hierarchy.
   */
  ownerNodeId?: string;
};

type LocationRow = {
  id: string;
  warehouse: Select2Option | null;
  zone: Select2Option | null;
  rack: Select2Option | null;
  bin: Select2Option | null;
  max_volume?: number;
  used_volume?: number;
  qty: number;
};

const SignageOfficeAgentInventoryProduct: React.FC<Props> = ({
  title,
  formik,
  brandOptions,
  categoryOptions,
  loadBrandOptions,
  loadCategoryOptions,
  selectedBrandOption = null,
  selectedCategoryOption = null,
  selectedBrand,
  setSelectedBrand,
  setSelectedCategories,
  conditionOptions,
  statusOptions,
  saving = false,
  onBack,
  onSubmit,
  previewFiles,
  uploadedFiles,
  onAddFiles,
  onRemovePreview,
  onRemoveUploaded,
  baseUrl,
  warehouseTable,
  mobileCardIds = { idColId: "warehouse_name", titleColId: "warehouse_name" },
  readOnly = false,
  hideWarehouseTable = false,
  hideSaveButton = false,
  disableQuantityField = false,
  requireBrandCategorySku = true,
  requireSku,
  resolveSrc,
  onQuantityChange,
  onLocationsChange,
  initialStorageLocations,
  showAgentDropdown = false,
  loadAgentOptions,
  selectedAgentOption,
  onAgentChange,
  showOfficeDropdown = false,
  officeOptions = [],
  selectedOfficeOption,
  onOfficeChange,
  type = "edit",
  ownerNodeId,
}) => {
    const [activeTab, setActiveTab] = useState<"details" | "images">("details");
  const tokenRef = useRef<string>(getAuthCookie("sessionToken") || "");
  const locationPath = useLocation();
  const isSignageTeammember = locationPath.pathname.includes("/signageteammember");
  const cookieUserId = getAuthCookie("userId") || "";
  const [resolvedOwnerNodeId, setResolvedOwnerNodeId] = useState<string>(
    ownerNodeId || (!isSignageTeammember ? cookieUserId : "")
  );

  // Resolve warehouse owner node: prop → signage userId → teammember parent signage
  useEffect(() => {
    if (ownerNodeId) {
      setResolvedOwnerNodeId(ownerNodeId);
      return;
    }
    if (!isSignageTeammember) {
      setResolvedOwnerNodeId(cookieUserId);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/parent-hierarchy`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${tokenRef.current}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        const signageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "SIGNAGE_FIRM"
        );
        if (!cancelled && signageFirm) {
          setResolvedOwnerNodeId(signageFirm.id || signageFirm._id || "");
        }
      } catch {
        // keep empty; warehouse select will stay empty until resolved
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerNodeId, isSignageTeammember, cookieUserId]);

  const mapSelect2Results = useCallback((json: any): { options: Select2Option[]; hasMore: boolean } => {
    const data = json?.data ?? json;
    const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
    const options: Select2Option[] = results.map((item: any) => ({
      value: String(item.id ?? item._id ?? ""),
      label: String(item.text ?? item.label ?? item.name ?? ""),
    })).filter((o: Select2Option) => o.value);
    const hasMore = Boolean(data?.pagination?.more);
    return { options, hasMore };
  }, []);

  const loadWarehouseOptions: LoadOptions<Select2Option, GroupBase<Select2Option>, AdditionalType> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      if (!resolvedOwnerNodeId) {
        return { options: [], hasMore: false, additional: { page } };
      }
      try {
        const params = new URLSearchParams({
          search: (inputValue || "").trim(),
          page: String(page),
          limit: String(PAGE_SIZE_LOCATION),
        });
        const url = `${process.env.REACT_APP_API_URL}/warehouses/owner-node/${resolvedOwnerNodeId}/select2?${params}`;
        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${tokenRef.current}`,
          },
        });
        if (!response.ok) {
          return { options: [], hasMore: false, additional: { page } };
        }
        const json = await response.json();
        const { options, hasMore } = mapSelect2Results(json);
        return { options, hasMore, additional: { page: page + 1 } };
      } catch {
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [resolvedOwnerNodeId, mapSelect2Results]
  );

  const loadZoneOptions = useCallback(
    (warehouseId: string): LoadOptions<Select2Option, GroupBase<Select2Option>, AdditionalType> =>
      async (inputValue, _loadedOptions, additional) => {
        const page = additional?.page ?? 1;
        if (!warehouseId) {
          return { options: [], hasMore: false, additional: { page } };
        }
        try {
          const params = new URLSearchParams({
            search: (inputValue || "").trim(),
            page: String(page),
            limit: String(PAGE_SIZE_LOCATION),
          });
          const url = `${process.env.REACT_APP_API_URL}/zones/warehouse/${warehouseId}/select2?${params}`;
          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${tokenRef.current}`,
            },
          });
          if (!response.ok) {
            return { options: [], hasMore: false, additional: { page } };
          }
          const json = await response.json();
          const { options, hasMore } = mapSelect2Results(json);
          return { options, hasMore, additional: { page: page + 1 } };
        } catch {
          return { options: [], hasMore: false, additional: { page } };
        }
      },
    [mapSelect2Results]
  );

  const loadRackOptions = useCallback(
    (zoneId: string): LoadOptions<Select2Option, GroupBase<Select2Option>, AdditionalType> =>
      async (inputValue, _loadedOptions, additional) => {
        const page = additional?.page ?? 1;
        if (!zoneId) {
          return { options: [], hasMore: false, additional: { page } };
        }
        try {
          const params = new URLSearchParams({
            search: (inputValue || "").trim(),
            page: String(page),
            limit: String(PAGE_SIZE_LOCATION),
          });
          const url = `${process.env.REACT_APP_API_URL}/racks/zone/${zoneId}/select2?${params}`;
          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${tokenRef.current}`,
            },
          });
          if (!response.ok) {
            return { options: [], hasMore: false, additional: { page } };
          }
          const json = await response.json();
          const { options, hasMore } = mapSelect2Results(json);
          return { options, hasMore, additional: { page: page + 1 } };
        } catch {
          return { options: [], hasMore: false, additional: { page } };
        }
      },
    [mapSelect2Results]
  );

  const loadBinOptions = useCallback(
    (rackId: string): LoadOptions<Select2Option, GroupBase<Select2Option>, AdditionalType> =>
      async (inputValue, _loadedOptions, additional) => {
        const page = additional?.page ?? 1;
        if (!rackId) {
          return { options: [], hasMore: false, additional: { page } };
        }
        try {
          const params = new URLSearchParams({
            search: (inputValue || "").trim(),
            page: String(page),
            limit: String(PAGE_SIZE_LOCATION),
          });
          const url = `${process.env.REACT_APP_API_URL}/bins/rack/${rackId}/select2?${params}`;
          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${tokenRef.current}`,
            },
          });
          if (!response.ok) {
            return { options: [], hasMore: false, additional: { page } };
          }
          const json = await response.json();
          const { options, hasMore } = mapSelect2Results(json);
          return { options, hasMore, additional: { page: page + 1 } };
        } catch {
          return { options: [], hasMore: false, additional: { page } };
        }
      },
    [mapSelect2Results]
  );

  const fetchBinVolume = useCallback(async (binId: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bins/${binId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenRef.current}`,
        },
      });
      if (!response.ok) return { max_volume: undefined as number | undefined, used_volume: undefined as number | undefined };
      const binData = await response.json();
      return {
        max_volume: binData?.max_volume as number | undefined,
        used_volume: binData?.used_volume as number | undefined,
      };
    } catch {
      return { max_volume: undefined as number | undefined, used_volume: undefined as number | undefined };
    }
  }, []);

  // ---------- Inventory rows / math ----------
  const [rows, setRows] = useState<LocationRow[]>([{
    id: crypto.randomUUID(),
    warehouse: null,
    zone: null,
    rack: null,
    bin: null,
    qty: 0,
  }]);
  const [rowsInitialized, setRowsInitialized] = useState(false);

  // Track if we've already initialized with volume data
  const [hasInitializedWithVolume, setHasInitializedWithVolume] = useState(false);

  // Initialize rows from initialStorageLocations
  useEffect(() => {
    if (!initialStorageLocations) return;

    const initialRows: LocationRow[] = [];

    initialStorageLocations.forEach((storage) => {
      if (Array.isArray(storage.location)) {
        storage.location.forEach((loc) => {
          initialRows.push({
            id: crypto.randomUUID(),
            warehouse: loc.warehouse_id
              ? { value: loc.warehouse_id, label: loc.warehouse_name || "Warehouse" }
              : null,
            zone: loc.zone_id
              ? { value: loc.zone_id, label: loc.zone_name || "Zone" }
              : null,
            rack: loc.rack_id
              ? { value: loc.rack_id, label: loc.rack_name || "Rack" }
              : null,
            bin: loc.bin_id
              ? { value: loc.bin_id, label: loc.bin_name || "Bin" }
              : null,
            max_volume: loc.max_volume,
            used_volume: loc.used_volume,
            qty: Number(loc.quantity || 0),
          });
        });
      }
    });

    if (initialRows.length > 0) {
      const hasVolumeData = initialRows.some(
        (row) =>
          (row.max_volume !== undefined && row.max_volume > 0) ||
          (row.used_volume !== undefined && row.used_volume > 0)
      );

      if (!rowsInitialized || (hasVolumeData && !hasInitializedWithVolume)) {
        setRows(initialRows);
        setRowsInitialized(true);
        if (hasVolumeData) {
          setHasInitializedWithVolume(true);
        }
      }
    }
  }, [initialStorageLocations, rowsInitialized, hasInitializedWithVolume]);

  // Backfill volume for initial bins that lack max/used
  useEffect(() => {
    if (!rowsInitialized) return;
    const missing = rows.filter(
      (r) => r.bin?.value && (r.max_volume === undefined || r.used_volume === undefined)
    );
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const updates = await Promise.all(
        missing.map(async (r) => {
          const vol = await fetchBinVolume(r.bin!.value);
          return { id: r.id, ...vol };
        })
      );
      if (cancelled) return;
      setRows((prev) =>
        prev.map((row) => {
          const u = updates.find((x) => x.id === row.id);
          return u
            ? { ...row, max_volume: u.max_volume ?? row.max_volume, used_volume: u.used_volume ?? row.used_volume }
            : row;
        })
      );
      setHasInitializedWithVolume(true);
    })();
    return () => {
      cancelled = true;
    };
    // only when rows first initialized
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsInitialized]);

  // Get productQty from formik values (TotalQuantity)
  const qtyFieldName = useMemo(() => {
    const v: any = formik?.values ?? {};
    return Object.prototype.hasOwnProperty.call(v, "qty") ? "qty" : "product.qty";
  }, [formik?.values]);

  const qtyValue: any = qtyFieldName === "qty"
    ? (formik?.values as any)?.qty
    : (formik?.values as any)?.product?.qty;

  const productQty = useMemo(() => Number(qtyValue || 0), [qtyValue]);
  
  const allocated = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.qty) || 0), 0),
    [rows]
  );
  const qtyDisabled = disableQuantityField || rows.length === 0;

  // Map row -> payload (stable). Include qty 0 when the full location is selected.
  const rowToPayload = useCallback(
    (row: LocationRow) => {
      if (!row.warehouse || !row.zone || !row.rack || !row.bin) return null;
      const quantity = Number(row.qty);
      if (!Number.isFinite(quantity) || quantity < 0) return null;
      return {
        quantity,
        warehouse_id: row.warehouse.value,
        warehouse_name: row.warehouse.label,
        zone_id: row.zone.value,
        zone_name: row.zone.label,
        rack_id: row.rack.value,
        rack_name: row.rack.label,
        bin_id: row.bin.value,
        bin_name: row.bin.label,
      };
    },
    []
  );

  // Build payload (memo) and emit to parent only when it actually changes
  const payload = useMemo(
    () => rows.map(rowToPayload).filter(Boolean) as NonNullable<ReturnType<typeof rowToPayload>>[],
    [rows, rowToPayload]
  );
  const lastEmitRef = useRef<string>("");
  useEffect(() => {
    if (!onLocationsChange) return;
    const signature = JSON.stringify({ payload, allocated });
    if (signature !== lastEmitRef.current) {
      lastEmitRef.current = signature;
      onLocationsChange(payload, allocated);
    }
  }, [payload, allocated, onLocationsChange]);

  // ---------- Handlers (stable with useCallback) ----------
  const addRow = useCallback(
    () =>
      setRows((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          warehouse: null,
          zone: null,
          rack: null,
          bin: null,
          qty: 0,
        },
      ]),
    []
  );

  const removeRow = useCallback(
    (id: string) => setRows((prev) => prev.filter((r) => r.id !== id)),
    []
  );

  const handleWarehouseChange = useCallback((id: string, selected: Select2Option | null) => {
    setRows((prev) =>
      prev.map((x) =>
        x.id === id
          ? {
              ...x,
              warehouse: selected,
              zone: null,
              rack: null,
              bin: null,
              max_volume: undefined,
              used_volume: undefined,
              qty: 0,
            }
          : x
      )
    );
  }, []);

  const handleZoneChange = useCallback((id: string, selected: Select2Option | null) => {
    setRows((prev) =>
      prev.map((x) =>
        x.id === id
          ? {
              ...x,
              zone: selected,
              rack: null,
              bin: null,
              max_volume: undefined,
              used_volume: undefined,
              qty: 0,
            }
          : x
      )
    );
  }, []);

  const handleRackChange = useCallback((id: string, selected: Select2Option | null) => {
    setRows((prev) =>
      prev.map((x) =>
        x.id === id
          ? {
              ...x,
              rack: selected,
              bin: null,
              max_volume: undefined,
              used_volume: undefined,
              qty: 0,
            }
          : x
      )
    );
  }, []);

  const handleBinChange = useCallback(
    async (id: string, selected: Select2Option | null) => {
      setRows((prev) =>
        prev.map((x) =>
          x.id === id
            ? {
                ...x,
                bin: selected,
                max_volume: undefined,
                used_volume: undefined,
                qty: 0,
              }
            : x
        )
      );
      if (!selected?.value) return;
      const vol = await fetchBinVolume(selected.value);
      setRows((prev) =>
        prev.map((x) =>
          x.id === id && x.bin?.value === selected.value
            ? { ...x, max_volume: vol.max_volume, used_volume: vol.used_volume }
            : x
        )
      );
    },
    [fetchBinVolume]
  );

  // ---------- Exceed modal (stable raiser) ----------
  const [showModal, setShowModal] = useState(false);
  const [modalText, setModalText] = useState("");

  const updateQty = useCallback(
    (id: string, val: string, _setExceed: (msg: string) => void) => {
      const next = Math.max(0, Number(val || 0));
      
      // Find the row being updated
      const currentRow = rows.find(r => r.id === id);
      if (!currentRow) {
        return;
      }
      
      // Calculate what the new total allocated would be
      const currentAllocated = rows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);
      const currentRowQty = Number(currentRow.qty || 0);
      const newAllocated = currentAllocated - currentRowQty + next;
      
      // Check if new total allocated exceeds productQty (only in edit mode)
      if (type === "edit" && newAllocated > productQty) {
        setModalText(`Total allocated quantity (${newAllocated}) cannot exceed Total Quantity (${productQty}). Please reduce the quantity.`);
        setShowModal(true);
        return; // Prevent update
      }
      
      // If bin is selected and volume is known, check available space
      if (currentRow.bin && currentRow.max_volume != null) {
        const maxVolume = currentRow.max_volume || 0;
        const usedVolume = currentRow.used_volume || 0;
        const availableVolume = maxVolume - usedVolume;
        
        if (next > availableVolume) {
          setModalText("No available space in this bin for the selected quantity. Please choose another bin.");
          setShowModal(true);
          return;
        }
      }
      
      // Allow update if validation passes
      setRows(prev => {
        const newRows = prev.map(r => (r.id === id ? { ...r, qty: next } : r));
        return newRows;
      });
    },
    [rows, productQty, type]
  );


// Note: Total Quantity should always show productQty from API, not allocated value
// We removed the sync to keep Total Quantity fixed at the API value

  // Handle Enter key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (showModal && event.key === "Enter") {
        event.preventDefault();
        setShowModal(false);
      }
    };

    if (showModal) {
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [showModal]);

  const raiseExceed = useCallback((msg: string) => {
    setModalText(msg);
    setShowModal(true);
  }, []);

  // ===================== UI =====================
  return (
    <div className="container-xxl py-3">
      {/* Tabs header */}
      <div className="d-flex detailsbutton align-items-center justify-content-between mb-3">
        <h4 className="mb-0">{title}</h4>
      </div>

      {/* Tabs */}
      <div className="rc-tabs mb-4">
        <button
          type="button"
          className={`rc-tab detailsbutton ${activeTab === "details" ? "rc-tab-active" : ""}`}
          onClick={() => setActiveTab("details")}
        >
          <i className="bi bi-box-seam me-2" /> Product Details
        </button>
        <button
          type="button"
          className={`rc-tab imagebutton ${activeTab === "images" ? "rc-tab-active" : ""} ms-3`}
          onClick={() => setActiveTab("images")}
        >
          <i className="bi bi-image me-2" /> Images
        </button>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
        noValidate
      >
        {activeTab === "details" && (
          <div className="box-shadow-datatables shadow-border-1 p-5">
            {/* top two columns (unchanged – product info/details) */}
            <div className="row g-4">
              {/* LEFT */}
              <div className="col-lg-6">
                <div className="font-medium header-color-product tracking-wide uppercase mb-4">
                  <div {...iconLabelProps(16)}>
                    <Package {...withIconStyle("#4a5565", 19)} />
                    <span className="header-color-product"> Product Information</span>
                  </div>
                </div>

                <div className="row g-3">
                  {showAgentDropdown && loadAgentOptions && (
                    <div className="col-md-12 mb-3">
                      <label className="form-label fw-semibold">
                        Select Agent <span className="text-danger">*</span>
                      </label>
                      <AsyncPaginate
                        classNamePrefix="rs"
                        placeholder="Search agents..."
                        value={selectedAgentOption ? {
                          value: selectedAgentOption.value,
                          label: selectedAgentOption.label,
                          firstName: selectedAgentOption.firstName,
                          lastName: selectedAgentOption.lastName,
                          email: selectedAgentOption.email,
                          phone: selectedAgentOption.phone
                        } : null}
                        loadOptions={loadAgentOptions}
                        onChange={(selected) => {
                          if (onAgentChange) {
                            onAgentChange(selected);
                          }
                          if (formik) {
                            formik.setFieldValue("agentSelection", selected?.value || "");
                          }
                        }}
                        isDisabled={readOnly}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            minHeight: "38px",
                            borderColor: formik?.touched?.agentSelection && formik?.errors?.agentSelection ? "#dc3545" : "#ced4da",
                          }),
                        }}
                      />
                      {formik?.touched?.agentSelection && formik?.errors?.agentSelection && (
                        <div className="text-danger small mt-1">{formik.errors.agentSelection}</div>
                      )}
                    </div>
                  )}
                  {showOfficeDropdown && (
                    <div className="col-md-12 mb-3">
                      <UIReactSelect
                        formik={formik}
                        name="officeSelection"
                        label="Select Office"
                        required
                        placeholder="Select office"
                        options={officeOptions}
                        disabled={readOnly}
                        onChange={(value: string) => {
                          const selected = officeOptions.find(opt => opt.value === value);
                          if (onOfficeChange) {
                            onOfficeChange(selected || null);
                          }
                        }}
                      />
                      {formik?.touched?.officeSelection && formik?.errors?.officeSelection && (
                        <div className="text-danger small mt-1">{formik.errors.officeSelection}</div>
                      )}
                    </div>
                  )}
                  <div className="col-md-6 mb-3">
                    {loadBrandOptions ? (
                      <>
                        <label className="form-label">
                          Brand{requireBrandCategorySku ? <span className="text-danger"> *</span> : null}
                        </label>
                        <AsyncPaginate
                          classNamePrefix="rs"
                          placeholder="Search or select brand..."
                          value={selectedBrandOption ? { value: selectedBrandOption.value, label: selectedBrandOption.label } : null}
                          loadOptions={loadBrandOptions}
                          additional={{ page: 1 }}
                          onChange={(selected: SelectOption | null) => {
                            if (selected) {
                              formik.setFieldValue("brand", selected.value, true);
                              setSelectedBrand?.(selected.value);
                              setSelectedCategories?.([]);
                            } else {
                              formik.setFieldValue("brand", "", true);
                              setSelectedBrand?.("");
                              setSelectedCategories?.([]);
                            }
                          }}
                          onBlur={() => formik.setFieldTouched("brand", true)}
                          isClearable
                          isDisabled={readOnly}
                          className={formik.touched?.brand && formik.errors?.brand ? "is-invalid" : ""}
                          styles={{
                            control: (base: any) => ({
                              ...base,
                              minHeight: 38,
                              borderRadius: 8,
                              borderColor: formik.touched?.brand && formik.errors?.brand ? "#dc3545" : base.borderColor,
                            }),
                          }}
                        />
                        {formik.touched?.brand && formik.errors?.brand && (
                          <div className="invalid-feedback d-block">{formik.errors.brand}</div>
                        )}
                      </>
                    ) : (
                      <UIReactSelect formik={formik} name="brand" label="Brand" placeholder="Select brand" options={brandOptions} disabled={readOnly} />
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    {loadCategoryOptions ? (
                      <>
                        <label className="form-label">
                          Category{requireBrandCategorySku ? <span className="text-danger"> *</span> : null}
                        </label>
                        <AsyncPaginate
                          classNamePrefix="rs"
                          placeholder={selectedBrand ? "Search or select category..." : "Select brand first"}
                          isDisabled={!selectedBrand || readOnly}
                          value={selectedCategoryOption ? { value: selectedCategoryOption.value, label: selectedCategoryOption.label } : null}
                          loadOptions={loadCategoryOptions}
                          additional={{ page: 1 }}
                          onChange={(selected: SelectOption | null) => {
                            if (selected) {
                              formik.setFieldValue("category", selected.value, true);
                              setSelectedCategories?.([selected.value]);
                            } else {
                              formik.setFieldValue("category", "", true);
                              setSelectedCategories?.([]);
                            }
                          }}
                          onBlur={() => formik.setFieldTouched("category", true)}
                          isClearable
                          key={selectedBrand || "no-brand"}
                          className={formik.touched?.category && formik.errors?.category ? "is-invalid" : ""}
                          styles={{
                            control: (base: any) => ({
                              ...base,
                              minHeight: 38,
                              borderRadius: 8,
                              borderColor: formik.touched?.category && formik.errors?.category ? "#dc3545" : base.borderColor,
                            }),
                          }}
                        />
                        {formik.touched?.category && formik.errors?.category && (
                          <div className="invalid-feedback d-block">{formik.errors.category}</div>
                        )}
                      </>
                    ) : (
                      <UIReactSelect formik={formik} name="category" label="Category" placeholder="Select category" options={categoryOptions} disabled={readOnly} />
                    )}
                  </div>
                </div>

                <div className="mt-3">
                  <label className="form-label fw-semibold">Product Description</label>
                  <textarea
                    id="description"
                    name="description"
                    className="form-control product-description-textarea"
                    rows={4}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    readOnly={readOnly}
                    disabled={readOnly}
                    style={{ borderRadius: 10, background: readOnly ? "#f3f4f6" : "#fafafa", borderColor: "#e5e7eb", fontSize: 14 }}
                    placeholder="Enter product description..."
                  />
                </div>
              </div>

              {/* RIGHT */}
              <div className="col-lg-6">
                <div className="font-medium header-color-product tracking-wide uppercase mb-4">
                  <div {...iconLabelProps(16)}>
                    <Package {...withIconStyle("#4a5565", 19)} />
                    <span className="header-color-product"> Product Details</span>
                  </div>
                </div>

                <div className="mb-3">
                  <UIInput formik={formik} name="name" label="Product Name" required placeholder="Enter product name" disabled={readOnly} />
                </div>

                <div className="row g-3">
                  <div className="col-md-6 mb-3 mt-3">
                    <UIInput formik={formik} name="sku" label="SKU" required={requireSku ?? requireBrandCategorySku} placeholder="Enter SKU" disabled={readOnly} />
                  </div>
                  
                  <div className="col-md-6 mb-3 mt-3">
                    <UIInput formik={formik} name="threshold_qty" label="Threshold Quantity" placeholder="0" disabled={readOnly} type="number" hideZero/>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="fw-semibold mb-2">Dimensions/Measurements (inches)</div>
                  <div className="row g-3">
                    <div className="col-md-4"><UIInput formik={formik} name="length" label="Length" placeholder="0.0" disabled={readOnly} type="number" hideZero /></div>
                    <div className="col-md-4"><UIInput formik={formik} name="height" label="Height" placeholder="0.0" disabled={readOnly} type="number" hideZero /></div>
                    <div className="col-md-4"><UIInput formik={formik} name="width"  label="Width"  placeholder="0.0" disabled={readOnly} type="number" hideZero /></div>
                    <div className="col-md-12"><UIInput formik={formik} name="weight" label="Weight (gms)" placeholder="0.0" disabled={readOnly} type="number" hideZero /></div>
                  </div>
                </div>

                <div className="row g-3 mt-1">
                  <div className="col-md-6 mb-3">
                    <UIReactSelect formik={formik} name="item_condition" label="Item Condition" placeholder="Select condition" options={conditionOptions} disabled={readOnly} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <UIReactSelect formik={formik} name="statuss" label="Status" options={statusOptions} placeholder="Select status" disabled={readOnly} />
                  </div>
                </div>
              </div>
            </div>

            {/* ================= Inventory & Locations ================= */}
            <div className="row">
            <div className="col-lg-3 mb-3 mt-3 d-flex align-items-center">
                    <label className="form-label col-lg-5">
                      Total Quantity
                    </label>
                    <input
                      type="number"
                      name={qtyFieldName}
                      className="form-control"
                      placeholder="0"
                      value={type === "add" ? allocated : productQty || ""}
                      readOnly
                      disabled
                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                  </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-1 mb-2">
              <h6 className="fw-semibold text-primary mb-0">Inventory &amp; Locations</h6>
              <button type="button" className="btn btn-primary table-addbutton" onClick={addRow} disabled={readOnly}>
                <i className="bi bi-plus-lg me-2" /> Add Location
              </button>
            </div>

            {/* Summary bar – matches screenshot (blue vs red) */}
            <div
              className="rounded-3 px-4 py-3 mb-3"
              style={{
                background: type === "edit" && allocated > productQty ? "#f4f4f4" : "#f4f4f4",
                border: `1px solid ${type === "edit" && allocated > productQty ? "#f5c2c7" : "#dbe4ff"}`
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div className="">
                  <i className="bi bi-cube me-2" />
                  Inventory Summary:
                </div>
                <div className={` ${type === "edit" && allocated > productQty ? "text-danger" : ""}`}>
                  {allocated} / {type === "add" ? allocated : productQty} units allocated
                </div>
              </div>
            </div>

            {/* Row cards */}
            {rows.map((r, i) => {
              const maxVolume = r.max_volume || 0;
              const usedVolume = r.used_volume || 0;
              const availableVolume =
                r.max_volume != null ? Math.max(0, maxVolume - usedVolume) : "";

              return (
                <div
                  key={r.id}
                  className="p-3 mb-3 rounded-4"
                  style={{ border: "1px solid #e9edf7", background: "#fff" }}
                >
                  <div className="row g-3 align-items-end">
                    <div className="col-md-3">
                      <div className="mb-2" style={{ color: "#101828" }}>
                        Warehouse #{i + 1}
                      </div>
                      <AsyncPaginate
                        classNamePrefix="rs"
                        placeholder="Search warehouse..."
                        isDisabled={readOnly || !resolvedOwnerNodeId}
                        value={r.warehouse}
                        loadOptions={loadWarehouseOptions}
                        onChange={(selected: Select2Option | null) => handleWarehouseChange(r.id, selected)}
                        additional={{ page: 1 }}
                        defaultOptions
                        isClearable
                        cacheUniqs={[resolvedOwnerNodeId]}
                        styles={locationSelectStyles}
                      />
                    </div>
                    <div className="col-md-3">
                      <div className="mb-2" style={{ color: "#101828" }}>Zone</div>
                      <AsyncPaginate
                        classNamePrefix="rs"
                        placeholder={r.warehouse ? "Search zone..." : "Select warehouse first"}
                        isDisabled={readOnly || !r.warehouse}
                        value={r.zone}
                        loadOptions={loadZoneOptions(r.warehouse?.value || "")}
                        onChange={(selected: Select2Option | null) => handleZoneChange(r.id, selected)}
                        additional={{ page: 1 }}
                        defaultOptions={Boolean(r.warehouse)}
                        isClearable
                        cacheUniqs={[r.warehouse?.value]}
                        styles={locationSelectStyles}
                      />
                    </div>
                    <div className="col-md-3">
                      <div className="mb-2" style={{ color: "#101828" }}>Rack</div>
                      <AsyncPaginate
                        classNamePrefix="rs"
                        placeholder={r.zone ? "Search rack..." : "Select zone first"}
                        isDisabled={readOnly || !r.zone}
                        value={r.rack}
                        loadOptions={loadRackOptions(r.zone?.value || "")}
                        onChange={(selected: Select2Option | null) => handleRackChange(r.id, selected)}
                        additional={{ page: 1 }}
                        defaultOptions={Boolean(r.zone)}
                        isClearable
                        cacheUniqs={[r.zone?.value]}
                        styles={locationSelectStyles}
                      />
                    </div>
                    <div className="col-md-3">
                      <div className="mb-2" style={{ color: "#101828" }}>Bin</div>
                      <AsyncPaginate
                        classNamePrefix="rs"
                        placeholder={r.rack ? "Search bin..." : "Select rack first"}
                        isDisabled={readOnly || !r.rack}
                        value={r.bin}
                        loadOptions={loadBinOptions(r.rack?.value || "")}
                        onChange={(selected: Select2Option | null) => handleBinChange(r.id, selected)}
                        additional={{ page: 1 }}
                        defaultOptions={Boolean(r.rack)}
                        isClearable
                        cacheUniqs={[r.rack?.value]}
                        styles={locationSelectStyles}
                      />
                    </div>
                  </div>

                  <div className="row g-3 align-items-end mt-1">
                    <div className="col-md-3">
                      <div className="mb-2" style={{ color: "#101828" }}>Available Quantity</div>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        value={availableVolume}
                        readOnly
                        disabled
                      />
                    </div>
                    <div className="col-md-3">
                      <div className="mb-2" style={{ color: "#101828" }}>Maximum Quantity</div>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        value={r.max_volume != null ? maxVolume : ""}
                        readOnly
                        disabled
                      />
                    </div>
                    <div className="col-md-3">
                      <div className="mb-2" style={{ color: "#101828" }}>Quantity</div>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        placeholder="0"
                        value={Number(r.qty ?? 0) === 0 ? "" : r.qty}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const nextQty = raw === "" ? 0 : Number(raw);
                          updateQty(
                            r.id,
                            String(Number.isFinite(nextQty) ? nextQty : 0),
                            raiseExceed
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        disabled={readOnly || !r.bin}
                      />
                    </div>
                    <div className="col-md-3 d-flex align-items-end justify-content-md-end">
                      <DeleteIconButton
                        type="button"
                        className="btn btn-link text-danger p-0 mb-2"
                        onClick={() => removeRow(r.id)}
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="col-12 mb-2"><hr /></div>

            <div className="d-flex justify-content-between align-items-center mt-2">
              <button type="button" onClick={onBack} className="btn back-btn surface-btn fw-bold fs-16">
                <i className="bi bi-chevron-left me-2" /> Back
              </button>
              {!hideSaveButton && (
                <button 
                  type="submit" 
                  className="btn btn-primary save-btn surface-btn fw-bold fs-16" 
                  disabled={saving || readOnly}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === "images" && (
          <div className="box-shadow-datatables shadow-border-1 p-5">
            <div className="">
              <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Image</h5>

              {!readOnly ? (
                <>
                  <div
                    className="border-1 rounded-4 p-4 text-center"
                    style={{ borderStyle: "dashed", background: "#fafafa" }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      onAddFiles(Array.from(e.dataTransfer.files || []) as File[]);
                    }}
                  >
                    <div className="mb-2"><i className="bi bi-upload fs-2" /></div>
                    <div className="mb-1"><span className="fw-semibold">Choose files</span> or drag and drop</div>
                    <div className="text-muted small">PNG, JPG up to 10MB each</div>
                    <div className="text-muted small">You can select multiple images at once</div>
                    <div className="mt-3">
                      <label className="btn btn-outline-primary mt-2">
                        Browse…
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          hidden
                          onChange={(e) => onAddFiles(Array.from(e.target.files || []) as File[])}
                        />
                      </label>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-muted small">Read-only: uploads are disabled.</div>
              )}

              {/* Saved images */}
              <div className="row mt-3">
                {(uploadedFiles ?? []).map((file, index) => {
                  const raw = file?.url;
                  const resolved = typeof resolveSrc === "function" ? resolveSrc(raw) : null;
                  const fallback =
                    typeof raw === "string" && raw.startsWith("http")
                      ? raw
                      : buildLegacyUrl(typeof raw === "string" ? raw : undefined) ?? null;
                  const src = resolved ?? fallback;
                  return (
                    <div key={`saved-${index}`} className="col-6 col-md-4 col-lg-3 mb-3">
                      <div className="img-card">
                        <button type="button" className="btn-close img-x" aria-label="Remove" onClick={() => onRemoveUploaded(index)} />
                        {src ? <img src={src} alt={file?.alt_text || "Image"} className="img-thumb" /> : <div className="text-muted small">Loading…</div>}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Previews */}
              <div className="row mt-1">
                {previewFiles.map((item, index) => (
                  <div key={`preview-${index}`} className="col-6 col-md-4 col-lg-3 mb-3">
                    <div className="img-card">
                      <button type="button" className="btn-close img-x" aria-label="Remove" onClick={() => onRemovePreview(index)} />
                      <img src={item.url} alt={`preview-${index}`} className="img-thumb" />
                    </div>
                    <div className="small text-center mt-1 text-muted text-truncate">{item.file.name}</div>
                  </div>
                ))}
              </div>

              <div className="col-12 mb-2"><hr /></div>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <button type="button" onClick={() => setActiveTab("details")} className="btn back-btn surface-btn fw-bold fs-16">
                  <i className="bi bi-arrow-left me-1" /> Back to Details
                </button>
                {!hideSaveButton && (
                  <button 
                    type="submit" 
                    className="btn btn-primary save-btn surface-btn fw-bold fs-16" 
                    disabled={saving || readOnly}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Styles needed for image tiles + tabs */}
        <style>{`
          .rc-tabs{display:flex;gap:8px;flex-wrap:wrap}
          .rc-tab{border:1px solid #e5e7eb;background:#f8f9fb;padding:8px 14px;border-radius:10px;font-weight:600;color:#374151}
          .rc-tab:hover{background:#eef2ff}

          .img-card{position:relative;border:1px solid #e5e7eb;border-radius:12px;background:#fff;height:180px;padding:8px;display:flex;align-items:center;justify-content:center}
          .img-thumb{max-width:100%;max-height:100%;object-fit:contain;border-radius:10px}
          .img-x{position:absolute;top:0;right:0;z-index:2;opacity:.9;width:28px;height:28px;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,.15);padding:0}
          .img-card:hover .img-x{opacity:1}
        `}</style>
      </form>

      {/* Validation Error modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.35)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body p-4">
                <div className="d-flex align-items-start">
                  <div className="me-2 text-danger fs-4"><i className="bi bi-exclamation-circle" /></div>
                  <div>
                    <h5 className="mb-2">Validation Error</h5>
                    <div className="">{modalText}</div>
                  </div>
                </div>
                <div className="text-end mt-3">
                  <button type="button" className="btn btn-primary" onClick={() => setShowModal(false)}>OK</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignageOfficeAgentInventoryProduct;
















// import React, { useEffect, useMemo, useRef, useState } from "react";
// import { UIInput, UIReactSelect } from "./WarehouseInputfields";
// import { Table, flexRender } from "@tanstack/react-table";
// import { Package } from "lucide-react";
// import { withIconStyle, iconLabelProps } from "components/ViewIcons";

// type SelectOption = { value: string; label: string };
// type PreviewFile = { file: File; url: string };
// type UploadedImage = { alt_text: string; url: string };
// type MobileCardIds = { idColId: string; titleColId: string; actionColId?: string };

// type Props = {
//   title: string;
//   formik: any;
//   brandOptions: SelectOption[];
//   categoryOptions: SelectOption[];
//   conditionOptions: SelectOption[];
//   statusOptions: SelectOption[];
//   saving?: boolean;
//   onBack: () => void;
//   onSubmit: () => void;
//   previewFiles: PreviewFile[];
//   uploadedFiles: UploadedImage[];
//   onAddFiles: (files: File[]) => void;
//   onRemovePreview: (idx: number) => void;
//   onRemoveUploaded: (idx: number) => void;
//   baseUrl?: string;
//   warehouseTable?: Table<any>;
//   mobileCardIds?: MobileCardIds;
//   readOnly?: boolean;
//   hideWarehouseTable?: boolean;
//   hideSaveButton?: boolean;
//   disableQuantityField?: boolean;
//   resolveSrc?: (url: string) => string | null;
//   onQuantityChange?: (value: string) => void;

//   // 🔹 new
//   onLocationsChange?: (
//     locs: {
//       quantity: number;
//       warehouse_id: string;
//       warehouse_name: string;
//       zone_id: string;
//       zone_name: string;
//       rack_id: string;
//       rack_name: string;
//       bin_id: string;
//       bin_name: string;
//     }[],
//     totalAllocated: number
//   ) => void;
// };

// type Warehouse = { _id: string; label: string };
// type Zone = { _id: string; label: string; warehouse_id: string };
// type Rack = { _id: string; label: string; zone_id: string };
// type Bin = { _id: string; label: string; rack_id: string };

// type Combo = {
//   warehouse_id: string;
//   warehouse: string;
//   zone_id: string;
//   zone: string;
//   rack_id: string;
//   rack: string;
//   bin_id: string;
//   bin: string;
// };

// type LocationRow = { id: string; comboBinId: string; qty: number };

// const SignageOfficeAgentInventoryProduct: React.FC<Props> = ({
//   title,
//   formik,
//   brandOptions,
//   categoryOptions,
//   conditionOptions,
//   statusOptions,
//   saving = false,
//   onBack,
//   onSubmit,
//   previewFiles,
//   uploadedFiles,
//   onAddFiles,
//   onRemovePreview,
//   onRemoveUploaded,
//   baseUrl,
//   warehouseTable,
//   mobileCardIds = { idColId: "warehouse_name", titleColId: "warehouse_name" },
//   readOnly = false,
//   hideWarehouseTable = false,
//   hideSaveButton = false,
//   disableQuantityField = false,
//   onQuantityChange,
//   onLocationsChange,
// }) => {
  // const [activeTab, setActiveTab] = useState<"details" | "images">("details");
  // const fileInputRef = useRef<HTMLInputElement | null>(null);
  // const token = getAuthCookie("sessionToken") || "";

  // // ===== API data
  // const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  // const [zones, setZones] = useState<Zone[]>([]);
  // const [racks, setRacks] = useState<Rack[]>([]);
  // const [bins, setBins] = useState<Bin[]>([]);
  // const [loading, setLoading] = useState(false);

  // const [rows, setRows] = useState<LocationRow[]>([{ id: crypto.randomUUID(), comboBinId: "", qty: 0 }]);
  // const [showModal, setShowModal] = useState(false);
  // const [modalText, setModalText] = useState("");

  // const fetchAll = async () => {
  //   try {
  //     setLoading(true);
  //     const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  //     const [w, z, r, b] = await Promise.all([
  //       fetch(`${process.env.REACT_APP_API_URL}/warehouses`, { headers }).then((r) => r.json()),
  //       fetch(`${process.env.REACT_APP_API_URL}/zones`, { headers }).then((r) => r.json()),
  //       fetch(`${process.env.REACT_APP_API_URL}/racks`, { headers }).then((r) => r.json()),
  //       fetch(`${process.env.REACT_APP_API_URL}/bins`, { headers }).then((r) => r.json()),
  //     ]);
  //     setWarehouses(w || []);
  //     setZones(z || []);
  //     setRacks(r || []);
  //     setBins(b || []);
  //   } catch (e) {
  //     console.error(e);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   fetchAll();
  // }, []);

  // // flatten combos
  // const combinations = useMemo(() => {
  //   const whMap: Record<string, string> = {};
  //   warehouses.forEach((w) => (whMap[w._id] = w.label));
  //   const out: Combo[] = [];
  //   racks.forEach((rk) => {
  //     const z = zones.find((zz) => zz._id === rk.zone_id);
  //     bins.forEach((bn) => {
  //       if (bn.rack_id === rk._id) {
  //         out.push({
  //           warehouse_id: z?.warehouse_id  || "",
  //           warehouse: whMap[z?.warehouse_id  || ""] || "",
  //           zone_id: z?._id || "",
  //           zone: z?.label || "",
  //           rack_id: rk._id,
  //           rack: rk.label,
  //           bin_id: bn._id,
  //           bin: bn.label,
  //         });
  //       }
  //     });
  //   });
  //   return out;
  // }, [warehouses, zones, racks, bins]);

  // // quantities
  // const productQty = Number(formik?.values?.qty || 0);
  // const allocated = rows.reduce((s, r) => s + (Number(r.qty) || 0), 0);
  // const qtyDisabled = disableQuantityField || rows.length === 0;

  // // convert rows -> payload
  // const rowToPayload = (row: LocationRow) => {
  //   const c = combinations.find((x) => x.bin_id === row.comboBinId);
  //   if (!c) return null;
  //   return {
  //     quantity: Number(row.qty || 0),
  //     warehouse_id: c.warehouse_id,
  //     warehouse_name: c.warehouse,
  //     zone_id: c.zone_id,
  //     zone_name: c.zone,
  //     rack_id: c.rack_id,
  //     rack_name: c.rack,
  //     bin_id: c.bin_id,
  //     bin_name: c.bin,
  //   };
  // };

  // useEffect(() => {
  //   const payload = rows.map(rowToPayload).filter(Boolean) as any[];
  //   onLocationsChange?.(payload, allocated);
  // }, [rows, combinations, allocated]);

  // const updateQty = (id: string, val: string) => {
  //   const n = Math.max(0, Number(val || 0));
  //   const newRows = rows.map((r) => (r.id === id ? { ...r, qty: n } : r));
  //   const newAlloc = newRows.reduce((s, r) => s + (Number(r.qty) || 0), 0);
  //   if (newAlloc > productQty) {
  //     setModalText(`Allocated ${newAlloc} exceeds total quantity ${productQty}`);
  //     setShowModal(true);
  //     return;
  //   }
  //   setRows(newRows);
  // };

  // const addRow = () => setRows([...rows, { id: crypto.randomUUID(), comboBinId: "", qty: 0 }]);
  // const removeRow = (id: string) => setRows(rows.filter((r) => r.id !== id));

  // const onQtyChange = (v: string) => {
  //   const next = Number(v || 0);
  //   if (next < allocated) {
  //     setModalText(`Product quantity ${next} is less than allocated ${allocated}`);
  //     setShowModal(true);
  //     return;
  //   }
  //   formik.setFieldValue("qty", next);
  //   onQuantityChange?.(v);
  // };

//   return (
//     <div className="container-xxl py-3">
//       <form
//         onSubmit={(e) => {
//           e.preventDefault();
//           onSubmit();
//         }}
//       >
//          {activeTab === "details" && (
//           <div className="box-shadow-datatables shadow-border-1 p-5">
//             <div className="">
//               <div className="row g-4">
//                 {/* LEFT */}
//                 <div className="col-lg-6">
//                   <div className="font-medium header-color-product tracking-wide uppercase mb-4">
//                     <div {...iconLabelProps(16)}>
//                       <Package {...withIconStyle("#4a5565", 19)} />

//                       <span className="header-color-product"> Product Information</span>
//                     </div>
//                   </div>

//                   <div className="row g-3">
//                     <div className="col-md-6 mb-3">
//                       <UIReactSelect
//                         formik={formik}
//                         name="brand"
//                         label="Brand"
//                         required
//                         placeholder="Select brand"
//                         options={brandOptions}
//                         disabled={readOnly}
//                       />
//                     </div>
//                     <div className="col-md-6 mb-3">
//                       <UIReactSelect
//                         formik={formik}
//                         name="category"
//                         label="Category"
//                         required
//                         placeholder="Select category"
//                         options={categoryOptions}
//                         disabled={readOnly}
//                       />
//                     </div>
//                   </div>

// <div className="mt-3">
//   <label className="form-label fw-semibold">Product Description</label>
//   <textarea
//     id="description"
//     name="description"
//     className="form-control"
//     rows={4}
//     value={formik.values.description}
//     onChange={formik.handleChange}
//     onBlur={formik.handleBlur}
//     readOnly={readOnly}
//     disabled={readOnly}
//     style={{
//       borderRadius: 10,
//       background: readOnly ? "#f3f4f6" : "#fafafa",
//       borderColor: "#e5e7eb",
//       fontSize: 14,
//     }}
//     placeholder="Enter product description..."
//   />
// </div>
//                 </div>

//                 {/* RIGHT */}
//                 <div className="col-lg-6">
//                   <div className="font-medium header-color-product tracking-wide uppercase mb-4">
//                     <div {...iconLabelProps(16)}>
//                       <Package {...withIconStyle("#4a5565", 19)} />

//                       <span className="header-color-product"> Product Details</span>
//                     </div>
//                   </div>
//                   <div className="mb-3">

//                     <UIInput
//                       formik={formik}
//                       name="name"
//                       label="Product Name"
//                       required
//                       placeholder="Enter product name"
//                       disabled={readOnly}
//                     />
//                   </div>
//                   <div className="row g-3">
//                     <div className="col-md-4 mb-3 mt-3
//                     ">
//                       <UIInput
//                         formik={formik}
//                         name="sku"
//                         label="SKU"
//                         required
//                         placeholder="Enter SKU"
//                         disabled={readOnly}
//                       />
//                     </div>
//                     <div className="col-md-4 mb-3 mt-3
//                     ">
//                       <UIInput
//                         formik={formik}
//                         name="qty"
//                         label="Quantity"
//                         required
//                         placeholder="0"
//                         disabled={readOnly}
//                       />
//                     </div>
//                     <div className="col-md-4 mb-3 mt-3
//                     ">
//                       <UIInput
//                         formik={formik}
//                         name="threshold_qty"
//                         label="Threshold Quantity"
//                         placeholder="0"
//                         disabled={readOnly}
//                       />
//                     </div>
//                   </div>

//                   <div className="mt-3">
//                     <div className="fw-semibold mb-2">Dimensions/Measurements (inches)</div>
//                     <div className="row g-3">
//                       <div className="col-md-4">
//                         <UIInput formik={formik} name="length" label="Length" placeholder="0.0" disabled={readOnly} />
//                       </div>
//                       <div className="col-md-4">
//                         <UIInput formik={formik} name="height" label="Height" placeholder="0.0" disabled={readOnly} />
//                       </div>
//                       <div className="col-md-4">
//                         <UIInput formik={formik} name="width" label="Width" placeholder="0.0" disabled={readOnly} />
//                       </div>
//                       <div className="col-md-12">
//                         <UIInput formik={formik} name="weight" label="Weight (gms)" placeholder="0.0" disabled={readOnly} />
//                       </div>
//                     </div>
//                   </div>

//                   <div className="row g-3 mt-1">
//                     <div className="col-md-6 mb-3">
//                       <UIReactSelect
//                         formik={formik}
//                         name="item_condition"
//                         label="Item Condition"
//                         placeholder="Select condition"
//                         options={conditionOptions}
//                         disabled={readOnly}
//                       />
//                     </div>
//                     <div className="col-md-6 mb-3">
//                       <UIReactSelect
//                         formik={formik}
//                         name="statuss"
//                         label="Status"
//                         required
//                         options={statusOptions}
//                         placeholder="Select status"
//                         disabled={readOnly}
//                       />
//                     </div>
//                   </div>
                  
//                 </div>
//               </div>

//               {/* --------------- Warehouse / Locations (responsive) --------------- */}

//               <div className="d-flex justify-content-between align-items-center mb-2">
//                   <h6 className="fw-semibold text-primary mb-0">Inventory & Locations</h6>
//                      <button type="button" className="btn btn-primary mt-2" onClick={addRow} disabled={readOnly || loading}>
//               <i className="bi bi-plus-lg me-2" />
//               Add Location
//             </button>
            
//                 </div>
//                <div
//               className={`d-flex justify-content-between align-items-center rounded-3 px-4 py-3 mb-3 ${
//                 allocated > productQty ? "bg-danger-subtle" : "bg-primary-subtle"
//               }`}
//               style={{
//                 background: allocated > productQty ? "#fde8e8" : "#f5f8ff",
//                 border: "1px solid #dce3ff",
//               }}
//             >
//               <div className="fw-semibold text-dark">
//                 <i className="bi bi-cube me-2 text-primary" />
//                 Inventory Summary
//               </div>
//               <div className={`fw-semibold ${allocated > productQty ? "text-danger" : "text-primary"}`}>
//                 {allocated} / {productQty} allocated
//               </div>
//             </div>
//              {rows.map((r, i) => (
//               <div
//                 key={r.id}
//                 className="p-3 mb-3 rounded-4 shadow-sm"
//                 style={{
//                   border: "1px solid #e3e9ff",
//                   background: "#ffffff",
//                   boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
//                 }}
//               >
//                 <div className="row g-3">
//                   <div className="col-md-6">
//                      <h6 className="fw-semibold text-primary mb-0">Warehouse Location #{i + 1}</h6>
//                     <select
//                       className="form-select"
//                       value={r.comboBinId}
//                       onChange={(e) =>
//                         setRows(rows.map((x) => (x.id === r.id ? { ...x, comboBinId: e.target.value } : x)))
//                       }
//                       disabled={readOnly || loading}
//                     >
//                       <option value="">Select Location</option>
//                       {combinations.map((c) => (
//                         <option key={c.bin_id} value={c.bin_id}>
//                           {c.warehouse} &gt; {c.zone} &gt; {c.rack} &gt; {c.bin}
//                         </option>
//                       ))}
//                     </select>
//                   </div>
//                   <div className="col-md-3">
//                     <label className="form-label fw-semibold small text-muted">Quantity</label>
//                     <input
//                       type="number"
//                       className="form-control"
//                       value={r.qty}
//                       min={0}
//                       onChange={(e) => updateQty(r.id, e.target.value)}
//                       disabled={readOnly || !r.comboBinId}
//                     />
//                   </div>
//                  <div className="col-md-3">
//                     <button
//                     type="button"
//                     className="btn btn-link text-danger p-0"
//                     onClick={() => removeRow(r.id)}
//                     disabled={readOnly}
//                   >
//                     <i className="bi bi-trash me-1" />
//                     Remove
//                   </button>
//                  </div>

//                 </div>
//               </div>
//             ))}

          
//               {showModal && (
//         <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.4)" }}>
//           <div className="modal-dialog modal-dialog-centered">
//             <div className="modal-content border-0">
//               <div className="modal-body p-4">
//                 <h6 className="fw-semibold text-danger mb-2">
//                   <i className="bi bi-exclamation-triangle me-2" />
//                   Quantity Exceeded
//                 </h6>
//                 <p className="text-muted mb-3">{modalText}</p>
//                 <div className="text-end">
//                   <button className="btn btn-primary" onClick={() => setShowModal(false)}>
//                     OK
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//               <div className="col-12 mb-2" id="row-overlap">
//                 <hr className="" />
//               </div>
//               <div className="d-flex justify-content-between align-items-center mt-4">
//                 <button
//                   type="button"
//                   onClick={() => onBack()}
//                   className="btn back-btn surface-btn fw-bold fs-16"
//                 >
//                   <i className="bi bi-chevron-left me-2" />
//                   Back
//                 </button>
//                 {!hideSaveButton && (
//                   <button type="submit" className="btn btn-primary save-btn surface-btn fw-bold fs-16" disabled={saving || readOnly}>
//                     {saving ? "Saving..." : "Save"}
//                   </button>
//                 )}
//               </div>
//             </div>
//           </div>
//         )}

//         {/* ================= IMAGES ================= */}
//         {activeTab === "images" && (
//           <div className="box-shadow-datatables shadow-border-1 p-5">
//             <div className="">
//               {/* Upload */}
//               <div className="mt-1">
//                 <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Image</h5>

//                 {!readOnly ? (
//                   <div
//                     className="border-1 rounded-4 p-4 text-center"
//                     style={{ borderStyle: "dashed", background: "#fafafa" }}
//                     onDragOver={(e) => e.preventDefault()}
//                     onDrop={(e) => {
//                       e.preventDefault();
//                       onAddFiles(Array.from(e.dataTransfer.files || []) as File[]);
//                     }}
//                   >
//                     <div className="mb-2">
//                       <i className="bi bi-upload fs-2" />
//                     </div>
//                     <div className="mb-1">
//                       <span className="fw-semibold">Choose files</span> or drag and drop
//                     </div>
//                     <div className="text-muted small">PNG, JPG up to 10MB each</div>
//                     <div className="text-muted small">You can select multiple images at once</div>
//                     <div className="mt-3">
//                       <label className="btn btn-outline-primary mt-2">
//                         Browse…
//                         <input
//                           type="file"
//                           accept="image/*"
//                           multiple
//                           hidden
//                           onChange={(e) => onAddFiles(Array.from(e.target.files || []) as File[])}
//                         />
//                       </label>
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="text-muted small">Read-only: uploads are disabled.</div>
//                 )}
//               </div>
//               {/* Previews */}
//               {previewFiles.length > 0 && (
//                 <div className="row g-3 mt-3">
//                   {previewFiles.map((item, index) => (
//                     <div key={`preview-${index}`} className="col-6 col-md-4 col-lg-3">
//                       <div className="img-card position-relative">
//                         <button
//                           type="button"
//                           className="btn-close img-x"
//                           aria-label="Remove"
//                           onClick={() => onRemovePreview(index)}
//                         />
//                         <img src={item.url} alt={`preview-${index}`} className="img-thumb" />
//                       </div>

//                       {/* filename under card, single line with … */}
//                       <div className="small text-center mt-1 text-muted text-truncate" title={item.file.name}>
//                         {item.file.name}
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )}

//               {uploadedFiles && uploadedFiles.length > 0 && (
//                 <>
//                   <div className="row g-3 mt-3">
//                     <div className="col-12 mb-1">
//                       <h6 className="text-muted">Already Uploaded Images</h6>
//                     </div>

//                     {uploadedFiles.map((file, index) => (
//                       <div key={`uploaded-${index}`} className="col-6 col-md-4 col-lg-3">
//                         <div className="img-card position-relative">
//                           <button
//                             type="button"
//                             className="btn-close img-x"
//                             aria-label="Remove"
//                             onClick={() => onRemoveUploaded(index)}
//                           />
//                           <img
//                             src={
//                               file.url?.startsWith("http")
//                                 ? file.url
//                                 : `${process.env.REACT_APP_BASE_URL}/${file.url?.replace(
//                                   /^https?:\/\/[^\/]+\/(uploads\/.*)/,
//                                   "$1"
//                                 )}`
//                             }
//                             alt={file.alt_text}
//                             className="img-thumb"
//                           />
//                         </div>
//                         <div className="small text-center mt-1 text-muted text-truncate">
//                           {file.alt_text || `Image ${index + 1}`}
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 </>
//               )}


//               {/* New preview images */}
//               {previewFiles.length === 0 && uploadedFiles.length === 0 && (
//                 <div className="col-12 text-center text-muted">
//                   <i className="bi bi-image fs-2 d-block mb-2" />
//                   No images uploaded yet
//                 </div>
//               )}
//               {/* Pending previews */}
//               {/* {previewFiles?.length > 0 && (
//                 <div className="row mt-3">
//                   {previewFiles.map((item, index) => (
//                     <div key={index} className="col-md-3 position-relative mb-3">
//                       {!readOnly && (
//                         <button
//                           type="button"
//                           className="btn-close position-absolute top-0 end-0 bg-white rounded-circle m-1"
//                           aria-label="Remove"
//                           onClick={() => onRemovePreview(index)}
//                         />
//                       )}
//                       <div className="border p-2 rounded-3">
//                         <img
//                           src={item.url}
//                           alt={`preview-${index}`}
//                           className="img-fluid"
//                           style={{ borderRadius: 8 }}
//                         />
//                         <div className="small text-center mt-1 text-muted">{item.file.name}</div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )} */}

//               {/* Uploaded */}
//               {/* <div className="mt-4">
//                 <div className="mb-2 text-muted">Uploaded</div>
//                 {uploadedFiles?.length === 0 ? (
//                   <div className="text-muted">No images uploaded yet.</div>
//                 ) : (
//                   <div className="row g-3">
//                     {uploadedFiles.map((img, idx) => (
//                       <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={`${img.url}-${idx}`}>
//                         <div className="position-relative border rounded overflow-hidden">
//                           <img
//                             src={fullSrc(img.url)}
//                             alt={img.alt_text}
//                             style={{ width: "100%", height: 120, objectFit: "contain" }}
//                           />
//                           {!readOnly && (
//                             <button
//                               type="button"
//                               className="btn btn-sm btn-danger position-absolute end-0 top-0 m-1"
//                               onClick={() => onRemoveUploaded(idx)}
//                             >
//                               <i className="bi bi-x-lg" />
//                             </button>
//                           )}
//                         </div>
//                         <div className="small mt-1 text-truncate" title={img.alt_text}>
//                           {img.alt_text}
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div> */}
//               <div className="col-12 mb-2" id="row-overlap">
//                 <hr className="" />
//               </div>
//               <div className="d-flex justify-content-between align-items-center mt-4">
//                 <button type="button" onClick={() => setActiveTab("details")} className="btn back-btn surface-btn fw-bold fs-16">
//                   <i className="bi bi-arrow-left me-1" />
//                   Back to Details
//                 </button>
//                 <div>
//                   {/* <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={onBack}>
//                     Cancel
//                   </button> */}
//                   {!hideSaveButton && (
//                     <button type="submit" className="btn btn-primary save-btn surface-btn fw-bold fs-16" disabled={saving || readOnly}>
//                       {saving ? "Saving..." : "Save"}
//                     </button>
//                   )}
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}
//         <style>{`
//         .rc-tabs{display:flex;gap:8px;flex-wrap:wrap}
//         .rc-tab{border:1px solid #e5e7eb;background:#f8f9fb;padding:8px 14px;border-radius:10px;font-weight:600;color:#374151}
//         .rc-tab:hover{background:#eef2ff}

//         .imageuploadui{border:2px dashed #d1d5db;border-radius:14px;padding:40px 12px;text-align:center;cursor:pointer;transition:all .2s}
//         .imageuploadui:hover{border-color:#6d28d9;background:#fbfaff}
//         .uploadedimages{border-radius:10px}
//           /* Thumbnail card so all X icons share the same anchor */
// .img-card{
//   position: relative;
//   border: 1px solid #e5e7eb;
//   border-radius: 12px;
//   background: #fff;
//   height: 180px;                 /* same height for all tiles */
//   padding: 8px;
//   display: flex;
//   align-items: center;
//   justify-content: center;
// }

// /* Image fits nicely without changing card height */
// .img-thumb{
//   max-width: 100%;
//   max-height: 100%;
//   object-fit: contain;
//   border-radius: 10px;
// }

// /* Close button perfectly aligned inside the tile */
// .img-x{
//   position: absolute;
//   top: 0px;
//   right: 0px;
//   z-index: 2;
//   opacity: 0.9;
//   width: 28px;
//   height: 28px;
//   border-radius: 50%;
//   background-color: #fff;
//   box-shadow: 0 1px 3px rgba(0,0,0,.15);
//   padding: 0;
// }

// /* optional: hover feedback */
// .img-card:hover .img-x{ opacity: 1; }

//         /* show/hide desktop vs mobile */
//         .rdt-desktop{display:block}
//         .rdt-mobile{display:none}
//         @media (max-width:1021px){
//           .rdt-desktop{display:none}
//           .rdt-mobile{display:block}
//         }
//       `}</style>
//       </form>
//     </div>
       
//   );
// };

// export default SignageOfficeAgentInventoryProduct;












// // SharedEditProductUI.tsx
// import React, { useState, useRef } from "react";
// import { UIInput, UIReactSelect } from "./WarehouseInputfields";
// import { Table, flexRender } from "@tanstack/react-table";
// import { Package, Settings } from 'lucide-react';
// import { withIconStyle, iconLabelProps } from "components/ViewIcons";
// type SelectOption = { value: string; label: string };
// type PreviewFile = { file: File; url: string };
// type UploadedImage = { alt_text: string; url: string };
// type MobileCardIds = { idColId: string; titleColId: string; actionColId?: string };

// type Props = {
//   title: string;
//   formik: any;
//   brandOptions: SelectOption[];
//   categoryOptions: SelectOption[];
//   conditionOptions: SelectOption[];
//   statusOptions: SelectOption[];

//   saving?: boolean;
//   onBack: () => void;
//   onSubmit: () => void;

//   // images (container controls the state)
//   previewFiles: PreviewFile[];
//   uploadedFiles: UploadedImage[];
//   onAddFiles: (files: File[]) => void;
//   onRemovePreview: (idx: number) => void;
//   onRemoveUploaded: (idx: number) => void;

//   baseUrl?: string;

//   // Warehouse table (optional): built in parent and passed here
//   warehouseTable?: Table<any>;
//   mobileCardIds?: MobileCardIds;

//   // NEW: view/edit controls
//   readOnly?: boolean;            // disables inputs/selects & image actions when true
//   hideWarehouseTable?: boolean;  // hides the warehouse table block
//   hideSaveButton?: boolean;      // hides Save button(s)
//   disableQuantityField?: boolean; // disables only the quantity field
//   // in Props:
//   resolveSrc?: (url: string) => string | null;
  
//   // Custom handlers
//   onQuantityChange?: (value: string) => void;

// };

// // External responsive helpers (adjust path if needed)
// import { MobileCardsOnly } from "./TableandMobilecomponent";

// const SharedEditProductUI: React.FC<Props> = ({
//   title,
//   formik,
//   brandOptions,
//   categoryOptions,
//   conditionOptions,
//   statusOptions,
//   saving = false,
//   onBack,
//   onSubmit,
//   previewFiles,
//   uploadedFiles,
//   onAddFiles,
//   onRemovePreview,
//   onRemoveUploaded,
//   baseUrl,
//   warehouseTable,
//   resolveSrc,
//   mobileCardIds = { idColId: "warehouse_name", titleColId: "warehouse_name", actionColId: "actions" },

//   // NEW defaults
//   readOnly = false,
//   hideWarehouseTable = false,
//   hideSaveButton = false,
//   disableQuantityField = false,
//   onQuantityChange,
// }) => {
//   const [activeTab, setActiveTab] = useState<"details" | "images">("details");
//   const fullSrc = (url: string) =>
//     /^https?:\/\//i.test(url) ? url : `${baseUrl || process.env.REACT_APP_BASE_URL || ""}${url}`;

//   const fileInputRef = useRef<HTMLInputElement | null>(null);

//   // Decide which field path to use for description: "description" or "product.description"
//   const descFieldName = React.useMemo(() => {
//     const v: any = formik?.values ?? {};
//     return Object.prototype.hasOwnProperty.call(v, "description")
//       ? "description"
//       : "product.description";
//   }, [formik?.values]);

//   // Read the current value safely
//   const descValue: string =
//     descFieldName === "description"
//       ? ((formik?.values as any)?.description ?? "")
//       : ((formik?.values as any)?.product?.description ?? "");

//   return (
//     <div className="container-xxl py-3">
//       {/* Header */}
//       <div className="d-flex detailsbutton align-items-center justify-content-between mb-3">
//         <h4 className="mb-0">{title}</h4>
//       </div>

//       {/* Tabs */}
//       <div className="rc-tabs mb-4">
//         <button
//           type="button"
//           className={`rc-tab detailsbutton  ${activeTab === "details" ? "rc-tab-active" : ""}`}
//           onClick={() => setActiveTab("details")}
//         >
//           <i className="bi bi-box-seam me-2" />
//           Product Details
//         </button>
//         <button
//           type="button"
//           className={`rc-tab imagebutton ${activeTab === "images" ? "rc-tab-active" : ""} ms-3`}
//           onClick={() => setActiveTab("images")}
//         >
//           <i className="bi bi-image me-2" />
//           Images
//         </button>
//       </div>

//       <form
//         onSubmit={(e) => {
//           e.preventDefault();
//           onSubmit();
//         }}
//         noValidate
//       >
//         {/* ================= DETAILS ================= */}
//         {activeTab === "details" && (
//           <div className="box-shadow-datatables shadow-border-1 p-5">
//             <div className="">
//               <div className="row g-4">
//                 {/* LEFT */}
//                 <div className="col-lg-6">
//                   <div className="font-medium header-color-product tracking-wide uppercase mb-4">
//                     <div {...iconLabelProps(16)}>
//                       <Package {...withIconStyle("#4a5565", 19)} />

//                       <span className="header-color-product"> Product Information</span>
//                     </div>
//                   </div>

//                   <div className="row g-3">
//                     <div className="col-md-6 mb-3">
//                       <UIReactSelect
//                         formik={formik}
//                         name="brand"
//                         label="Brand"
//                         required
//                         placeholder="Select brand"
//                         options={brandOptions}
//                         disabled={readOnly}
//                       />
//                     </div>
//                     <div className="col-md-6 mb-3">
//                       <UIReactSelect
//                         formik={formik}
//                         name="category"
//                         label="Category"
//                         required
//                         placeholder="Select category"
//                         options={categoryOptions}
//                         disabled={readOnly}
//                       />
//                     </div>
//                   </div>

//                   <div className="mt-3">
//                     <label className="form-label fw-semibold">Product Description</label>
//                     <textarea
//                       id="description"
//                       name={descFieldName}                 // ✅ points to the right path
//                       className="form-control"
//                       rows={4}
//                       value={descValue}                    // ✅ safe for both shapes
//                       onChange={formik.handleChange}
//                       onBlur={formik.handleBlur}
//                       readOnly={readOnly}
//                       disabled={readOnly}
//                       style={{
//                         borderRadius: 10,
//                         background: readOnly ? "#f3f4f6" : "#fafafa",
//                         borderColor: "#e5e7eb",
//                         fontSize: 14,
//                       }}
//                       placeholder="Enter product description..."
//                     />
//                   </div>

//                 </div>

//                 {/* RIGHT */}
//                 <div className="col-lg-6">
//                   <div className="font-medium header-color-product tracking-wide uppercase mb-4">
//                     <div {...iconLabelProps(16)}>
//                       <Package {...withIconStyle("#4a5565", 19)} />

//                       <span className="header-color-product"> Product Details</span>
//                     </div>
//                   </div>
//                   <div className="mb-3">

//                     <UIInput
//                       formik={formik}
//                       name="name"
//                       label="Product Name"
//                       required
//                       placeholder="Enter product name"
//                       disabled={readOnly}
//                     />
//                   </div>
//                   <div className="row g-3">
//                     <div className="col-md-4 mb-3 mt-3
//                     ">
//                       <UIInput
//                         formik={formik}
//                         name="sku"
//                         label="SKU"
//                         required
//                         placeholder="Enter SKU"
//                         disabled={readOnly}
//                       />
//                     </div>
//                     <div className="col-md-4 mb-3 mt-3
//                     ">
//                       <UIInput
//                         formik={formik}
//                         name="qty"
//                         label="Quantity"
//                         required
//                         placeholder="0"
//                         disabled={readOnly || disableQuantityField}
//                         onChange={onQuantityChange || formik.handleChange}
//                       />
//                     </div>
//                     <div className="col-md-4 mb-3 mt-3
//                     ">
//                       <UIInput
//                         formik={formik}
//                         name="threshold_qty"
//                         label="Threshold Quantity"
//                         placeholder="0"
//                         disabled={readOnly}
//                       />
//                     </div>
//                   </div>

//                   <div className="mt-3">
//                     <div className="fw-semibold mb-2">Dimensions/Measurements (inches)</div>
//                     <div className="row g-3">
//                       <div className="col-md-4">
//                         <UIInput formik={formik} name="length" label="Length" placeholder="0.0" disabled={readOnly} />
//                       </div>
//                       <div className="col-md-4">
//                         <UIInput formik={formik} name="height" label="Height" placeholder="0.0" disabled={readOnly} />
//                       </div>
//                       <div className="col-md-4">
//                         <UIInput formik={formik} name="width" label="Width" placeholder="0.0" disabled={readOnly} />
//                       </div>
//                       <div className="col-md-12">
//                         <UIInput formik={formik} name="weight" label="Weight (gms)" placeholder="0.0" disabled={readOnly} />
//                       </div>
//                     </div>
//                   </div>

//                   <div className="row g-3 mt-1">
//                     <div className="col-md-6 mb-3">
//                       <UIReactSelect
//                         formik={formik}
//                         name="item_condition"
//                         label="Item Condition"
//                         placeholder="Select condition"
//                         options={conditionOptions}
//                         disabled={readOnly}
//                       />
//                     </div>
//                     <div className="col-md-6 mb-3">
//                       <UIReactSelect
//                         formik={formik}
//                         name="statuss"
//                         label="Status"
//                         required
//                         options={statusOptions}
//                         placeholder="Select status"
//                         disabled={readOnly}
//                       />
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* --------------- Warehouse / Locations (responsive) --------------- */}
//               {!!warehouseTable && !hideWarehouseTable && (
//                 <div className="mt-5">
//                   <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Inventory & Locations</h5>

//                   {/* DESKTOP/TABLET */}
//                   <div className="rdt-desktop">
//                     <div className="table-responsive">
//                       <table className="table table-hover align-middle">
//                         <thead>
//                           {warehouseTable.getHeaderGroups().map((hg) => (
//                             <tr key={hg.id}>
//                               {hg.headers.map((header) => (
//                                 <th key={header.id} style={{ whiteSpace: "nowrap" }}>
//                                   {header.isPlaceholder
//                                     ? null
//                                     : flexRender(header.column.columnDef.header, header.getContext())}
//                                 </th>
//                               ))}
//                             </tr>
//                           ))}
//                         </thead>
//                         <tbody>
//                           {warehouseTable.getRowModel().rows.map((row) => (
//                             <tr key={row.id}>
//                               {row.getVisibleCells().map((cell) => (
//                                 <td key={cell.id} style={{ verticalAlign: "middle" }}>
//                                   {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                                 </td>
//                               ))}
//                             </tr>
//                           ))}
//                         </tbody>
//                       </table>
//                     </div>
//                   </div>

//                   {/* MOBILE (≤ 1021px) */}
//                   <div className="rdt-mobile">
//                     <MobileCardsOnly
//                       table={warehouseTable as any}
//                       idColId={mobileCardIds.idColId}
//                       titleColId={mobileCardIds.titleColId}
//                       actionColId={mobileCardIds.actionColId || "actions"}
//                     />
//                   </div>

//                   {/* Empty / Pagination */}
//                   {warehouseTable.getRowModel().rows.length === 0 ? (
//                     <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
//                       No records found
//                     </div>
//                   ) : (
//                     <div className="mt-4 mb-4">{/* pagination slot */}</div>
//                   )}
//                 </div>
//               )}

//               <div className="col-12 mb-2" id="row-overlap">
//                 <hr className="" />
//               </div>
//               <div className="d-flex justify-content-between align-items-center mt-4">
//                 <button
//                   type="button"
//                   onClick={() => onBack()}
//                   className="btn back-btn surface-btn fw-bold fs-16"
//                 >
//                   <i className="bi bi-chevron-left me-2" />
//                   Back
//                 </button>
//                 {!hideSaveButton && (
//                   <button type="submit" className="btn btn-primary save-btn surface-btn fw-bold fs-16" disabled={saving || readOnly}>
//                     {saving ? "Saving..." : "Save"}
//                   </button>
//                 )}
//               </div>
//             </div>
//           </div>
//         )}

//         {activeTab === "images" && (
//           <div className="card shadow-border-2">
//             <div className="card-body">
//               <h6 className="mb-3">Product Images</h6>

//               {!readOnly ? (
//                 <>
//                   <div
//                     className="imageuploadui"
//                     onClick={() => fileInputRef.current?.click()}
//                     role="button"
//                     tabIndex={0}
//                   >
//                     <i className="bi bi-cloud-arrow-up fs-2 d-block mb-2" />
//                     <div className="fw-semibold">Upload Images</div>
//                     <div className="small text-muted">Drag & drop images here, or click to select files</div>
//                   </div>

//                   <input
//                     type="file"
//                     multiple
//                     ref={fileInputRef}
//                     style={{ display: "none" }}
//                     onChange={(e) => {
//                       const files = Array.from(e.target.files || []);
//                       onAddFiles(files);            // ✅ use prop
//                       e.currentTarget.value = "";   // allow re-pick same file
//                     }}
//                   />
//                 </>
//               ) : (
//                 <div className="text-muted small">Read-only: uploads are disabled.</div>
//               )}
//               {/* EXISTING (SAVED) IMAGES */}
//               <div className="row mt-3">
//                 {(uploadedFiles ?? []).length === 0 && previewFiles.length === 0 && (
//                   <div className="col-12 text-center text-muted">
//                     <i className="bi bi-image fs-2 d-block mb-2" />
//                     No images yet
//                   </div>
//                 )}

//                 {(uploadedFiles ?? []).map((file, index) => {
//                   const raw = file?.url;
//                   console.log(raw,"rawss");
//                   const src =
//                     // prefer a resolver if parent passed one; else fallback to baseUrl
//                     (typeof resolveSrc === "function" ? resolveSrc(raw) : null) ||
//                     (typeof raw === "string" && raw.startsWith("http")
//                       ? raw
//                       : `${baseUrl || process.env.REACT_APP_BASE_URL || ""}/${String(raw || "").replace(
//                         /^https?:\/\/[^/]+\/(uploads\/.*)/,
//                         "$1"
//                       )}`);
//                       console.log(src,"src");

//                   return (
//                     <div key={`saved-${index}`} className="col-6 col-md-4 col-lg-3 mb-3">
//                       <div className="img-card">
//                         <button
//                           type="button"
//                           className="btn-close img-x"
//                           aria-label="Remove"
//                           onClick={() => onRemoveUploaded(index)}     // ✅ index to parent
//                         />
//                         {src ? (
//                           <img src={src} alt={file?.alt_text || "Image"} className="img-thumb" />
//                         ) : (
//                           <div className="text-muted small">Loading…</div>
//                         )}
//                       </div>
//                     </div>
//                   );
//                 })}
//               </div>

//               {/* NEWLY-SELECTED PREVIEW IMAGES (not uploaded yet) */}
//               <div className="row mt-1">
//                 {previewFiles.map((item, index) => (
//                   <div key={`preview-${index}`} className="col-6 col-md-4 col-lg-3 mb-3">
//                     <div className="img-card">
//                       <button
//                         type="button"
//                         className="btn-close img-x"
//                         aria-label="Remove"
//                         onClick={() => onRemovePreview(index)}        // ✅ index to parent
//                       />
//                       <img src={item.url} alt={`preview-${index}`} className="img-thumb" />
//                     </div>
//                     <div className="small text-center mt-1 text-muted text-truncate">
//                       {item.file.name}
//                     </div>
//                   </div>
//                 ))}
//               </div>


//               {!hideSaveButton && (
//                 <div className="d-flex justify-content-end mt-3">
//                   <button
//                     className="btn btn-primary"
//                     onClick={formik.submitForm}
//                     disabled={formik.isSubmitting}
//                   >
//                     {formik.isSubmitting ? "Saving..." : "Save"}
//                   </button>

//                 </div>
//               )}
//             </div>

//           </div>
//         )}
//         <style>{`
//         .rc-tabs{display:flex;gap:8px;flex-wrap:wrap}
//         .rc-tab{border:1px solid #e5e7eb;background:#f8f9fb;padding:8px 14px;border-radius:10px;font-weight:600;color:#374151}
//         .rc-tab:hover{background:#eef2ff}

//         .imageuploadui{border:2px dashed #d1d5db;border-radius:14px;padding:40px 12px;text-align:center;cursor:pointer;transition:all .2s}
//         .imageuploadui:hover{border-color:#6d28d9;background:#fbfaff}
//         .uploadedimages{border-radius:10px}
//           /* Thumbnail card so all X icons share the same anchor */
// .img-card{
//   position: relative;
//   border: 1px solid #e5e7eb;
//   border-radius: 12px;
//   background: #fff;
//   height: 180px;                 /* same height for all tiles */
//   padding: 8px;
//   display: flex;
//   align-items: center;
//   justify-content: center;
// }

// /* Image fits nicely without changing card height */
// .img-thumb{
//   max-width: 100%;
//   max-height: 100%;
//   object-fit: contain;
//   border-radius: 10px;
// }

// /* Close button perfectly aligned inside the tile */
// .img-x{
//   position: absolute;
//   top: 0px;
//   right: 0px;
//   z-index: 2;
//   opacity: 0.9;
//   width: 28px;
//   height: 28px;
//   border-radius: 50%;
//   background-color: #fff;
//   box-shadow: 0 1px 3px rgba(0,0,0,.15);
//   padding: 0;
// }

// /* optional: hover feedback */
// .img-card:hover .img-x{ opacity: 1; }

//         /* show/hide desktop vs mobile */
//         .rdt-desktop{display:block}
//         .rdt-mobile{display:none}
//         @media (max-width:1021px){
//           .rdt-desktop{display:none}
//           .rdt-mobile{display:block}
//         }
//       `}</style>
//       </form>
//     </div>
//   );
// };

// export default SharedEditProductUI;
















// // SharedEditProductUI.tsx
// import React, { useState } from "react";
// import { UIInput, UIReactSelect } from "./WarehouseInputfields";
// import { Table, flexRender } from "@tanstack/react-table";
// import { Package, Settings } from 'lucide-react';
// import { withIconStyle, iconLabelProps } from "components/ViewIcons";
// type SelectOption = { value: string; label: string };
// type PreviewFile = { file: File; url: string };
// type UploadedImage = { alt_text: string; url: string };
// type MobileCardIds = { idColId: string; titleColId: string; actionColId?: string };

// type Props = {
//   title: string;
//   formik: any;
//   brandOptions: SelectOption[];
//   categoryOptions: SelectOption[];
//   conditionOptions: SelectOption[];
//   statusOptions: SelectOption[];

//   saving?: boolean;
//   onBack: () => void;
//   onSubmit: () => void;

//   // images (container controls the state)
//   previewFiles: PreviewFile[];
//   uploadedFiles: UploadedImage[];
//   onAddFiles: (files: File[]) => void;
//   onRemovePreview: (idx: number) => void;
//   onRemoveUploaded: (idx: number) => void;

//   baseUrl?: string;

//   // Warehouse table (optional): built in parent and passed here
//   warehouseTable?: Table<any>;
//   mobileCardIds?: MobileCardIds;

//   // NEW: view/edit controls
//   readOnly?: boolean;            // disables inputs/selects & image actions when true
//   hideWarehouseTable?: boolean;  // hides the warehouse table block
//   hideSaveButton?: boolean;      // hides Save button(s)
// };

// // External responsive helpers (adjust path if needed)
// import { MobileCardsOnly } from "./TableandMobilecomponent";

// const SharedEditProductUI: React.FC<Props> = ({
//   title,
//   formik,
//   brandOptions,
//   categoryOptions,
//   conditionOptions,
//   statusOptions,
//   saving = false,
//   onBack,
//   onSubmit,
//   previewFiles,
//   uploadedFiles,
//   onAddFiles,
//   onRemovePreview,
//   onRemoveUploaded,
//   baseUrl,
//   warehouseTable,
//   mobileCardIds = { idColId: "warehouse_name", titleColId: "warehouse_name", actionColId: "actions" },

//   // NEW defaults
//   readOnly = false,
//   hideWarehouseTable = false,
//   hideSaveButton = false,
// }) => {
//   const [activeTab, setActiveTab] = useState<"details" | "images">("details");

//   const fullSrc = (url: string) =>
//     /^https?:\/\//i.test(url) ? url : `${baseUrl || process.env.REACT_APP_BASE_URL || ""}${url}`;

//   console.log("uploadedFiles:", uploadedFiles);
//   return (
//     <div className="container-xxl py-3">
//       {/* Header */}
//       <div className="d-flex detailsbutton align-items-center justify-content-between mb-3">
//         <h4 className="mb-0">{title}</h4>
//       </div>

//       {/* Tabs */}
//       <div className="rc-tabs mb-4">
//         <button
//           type="button"
//           className={`rc-tab detailsbutton  ${activeTab === "details" ? "rc-tab-active" : ""}`}
//           onClick={() => setActiveTab("details")}
//         >
//           <i className="bi bi-box-seam me-2" />
//           Product Details
//         </button>
//         <button
//           type="button"
//           className={`rc-tab imagebutton ${activeTab === "images" ? "rc-tab-active" : ""} ms-3`}
//           onClick={() => setActiveTab("images")}
//         >
//           <i className="bi bi-image me-2" />
//           Images
//         </button>
//       </div>

//       <form
//         onSubmit={(e) => {
//           e.preventDefault();
//           onSubmit();
//         }}
//         noValidate
//       >
//         {/* ================= DETAILS ================= */}
//         {activeTab === "details" && (
//           <div className="box-shadow-datatables shadow-border-1 p-5">
//             <div className="">
//               <div className="row g-4">
//                 {/* LEFT */}
//                 <div className="col-lg-6">
//                   <div className="font-medium header-color-product tracking-wide uppercase mb-4">
//                     <div {...iconLabelProps(16)}>
//                       <Package {...withIconStyle("#4a5565", 19)} />

//                       <span className="header-color-product"> Product Information</span>
//                     </div>
//                   </div>

//                   <div className="row g-3">
//                     <div className="col-md-6 mb-3">
//                       <UIReactSelect
//                         formik={formik}
//                         name="brand"
//                         label="Brand"
//                         required
//                         placeholder="Select brand"
//                         options={brandOptions}
//                         disabled={readOnly}
//                       />
//                     </div>
//                     <div className="col-md-6 mb-3">
//                       <UIReactSelect
//                         formik={formik}
//                         name="category"
//                         label="Category"
//                         required
//                         placeholder="Select category"
//                         options={categoryOptions}
//                         disabled={readOnly}
//                       />
//                     </div>
//                   </div>

// <div className="mt-3">
//   <label className="form-label fw-semibold">Product Description</label>
//   <textarea
//     id="description"
//     name="description"
//     className="form-control"
//     rows={4}
//     value={formik.values.description}
//     onChange={formik.handleChange}
//     onBlur={formik.handleBlur}
//     readOnly={readOnly}
//     disabled={readOnly}
//     style={{
//       borderRadius: 10,
//       background: readOnly ? "#f3f4f6" : "#fafafa",
//       borderColor: "#e5e7eb",
//       fontSize: 14,
//     }}
//     placeholder="Enter product description..."
//   />
// </div>
//                 </div>

//                 {/* RIGHT */}
//                 <div className="col-lg-6">
//                   <div className="font-medium header-color-product tracking-wide uppercase mb-4">
//                     <div {...iconLabelProps(16)}>
//                       <Package {...withIconStyle("#4a5565", 19)} />

//                       <span className="header-color-product"> Product Details</span>
//                     </div>
//                   </div>
//                   <div className="mb-3">

//                     <UIInput
//                       formik={formik}
//                       name="name"
//                       label="Product Name"
//                       required
//                       placeholder="Enter product name"
//                       disabled={readOnly}
//                     />
//                   </div>
//                   <div className="row g-3">
//                     <div className="col-md-4 mb-3 mt-3
//                     ">
//                       <UIInput
//                         formik={formik}
//                         name="sku"
//                         label="SKU"
//                         required
//                         placeholder="Enter SKU"
//                         disabled={readOnly}
//                       />
//                     </div>
//                     <div className="col-md-4 mb-3 mt-3
//                     ">
//                       <UIInput
//                         formik={formik}
//                         name="qty"
//                         label="Quantity"
//                         required
//                         placeholder="0"
//                         disabled={readOnly}
//                       />
//                     </div>
//                     <div className="col-md-4 mb-3 mt-3
//                     ">
//                       <UIInput
//                         formik={formik}
//                         name="threshold_qty"
//                         label="Threshold Quantity"
//                         placeholder="0"
//                         disabled={readOnly}
//                       />
//                     </div>
//                   </div>

//                   <div className="mt-3">
//                     <div className="fw-semibold mb-2">Dimensions/Measurements (inches)</div>
//                     <div className="row g-3">
//                       <div className="col-md-4">
//                         <UIInput formik={formik} name="length" label="Length" placeholder="0.0" disabled={readOnly} />
//                       </div>
//                       <div className="col-md-4">
//                         <UIInput formik={formik} name="height" label="Height" placeholder="0.0" disabled={readOnly} />
//                       </div>
//                       <div className="col-md-4">
//                         <UIInput formik={formik} name="width" label="Width" placeholder="0.0" disabled={readOnly} />
//                       </div>
//                       <div className="col-md-12">
//                         <UIInput formik={formik} name="weight" label="Weight (gms)" placeholder="0.0" disabled={readOnly} />
//                       </div>
//                     </div>
//                   </div>

//                   <div className="row g-3 mt-1">
//                     <div className="col-md-6 mb-3">
//                       <UIReactSelect
//                         formik={formik}
//                         name="item_condition"
//                         label="Item Condition"
//                         placeholder="Select condition"
//                         options={conditionOptions}
//                         disabled={readOnly}
//                       />
//                     </div>
//                     <div className="col-md-6 mb-3">
//                       <UIReactSelect
//                         formik={formik}
//                         name="statuss"
//                         label="Status"
//                         required
//                         options={statusOptions}
//                         placeholder="Select status"
//                         disabled={readOnly}
//                       />
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* --------------- Warehouse / Locations (responsive) --------------- */}
//               {!!warehouseTable && !hideWarehouseTable && (
//                 <div className="mt-5">
//                   <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Inventory & Locations</h5>

//                   {/* DESKTOP/TABLET */}
//                   <div className="rdt-desktop">
//                     <div className="table-responsive">
//                       <table className="table table-hover align-middle">
//                         <thead>
//                           {warehouseTable.getHeaderGroups().map((hg) => (
//                             <tr key={hg.id}>
//                               {hg.headers.map((header) => (
//                                 <th key={header.id} style={{ whiteSpace: "nowrap" }}>
//                                   {header.isPlaceholder
//                                     ? null
//                                     : flexRender(header.column.columnDef.header, header.getContext())}
//                                 </th>
//                               ))}
//                             </tr>
//                           ))}
//                         </thead>
//                         <tbody>
//                           {warehouseTable.getRowModel().rows.map((row) => (
//                             <tr key={row.id}>
//                               {row.getVisibleCells().map((cell) => (
//                                 <td key={cell.id} style={{ verticalAlign: "middle" }}>
//                                   {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                                 </td>
//                               ))}
//                             </tr>
//                           ))}
//                         </tbody>
//                       </table>
//                     </div>
//                   </div>

//                   {/* MOBILE (≤ 1021px) */}
//                   <div className="rdt-mobile">
//                     <MobileCardsOnly
//                       table={warehouseTable as any}
//                       idColId={mobileCardIds.idColId}
//                       titleColId={mobileCardIds.titleColId}
//                       actionColId={mobileCardIds.actionColId || "actions"}
//                     />
//                   </div>

//                   {/* Empty / Pagination */}
//                   {warehouseTable.getRowModel().rows.length === 0 ? (
//                     <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
//                       No records found
//                     </div>
//                   ) : (
//                     <div className="mt-4 mb-4">{/* pagination slot */}</div>
//                   )}
//                 </div>
//               )}

//               <div className="col-12 mb-2" id="row-overlap">
//                 <hr className="" />
//               </div>
//               <div className="d-flex justify-content-between align-items-center mt-4">
//                 <button
//                   type="button"
//                   onClick={() => onBack()}
//                   className="btn back-btn surface-btn fw-bold fs-16"
//                 >
//                   <i className="bi bi-chevron-left me-2" />
//                   Back
//                 </button>
//                 {!hideSaveButton && (
//                   <button type="submit" className="btn btn-primary save-btn surface-btn fw-bold fs-16" disabled={saving || readOnly}>
//                     {saving ? "Saving..." : "Save"}
//                   </button>
//                 )}
//               </div>
//             </div>
//           </div>
//         )}

//         {/* ================= IMAGES ================= */}
//         {activeTab === "images" && (
//           <div className="box-shadow-datatables shadow-border-1 p-5">
//             <div className="">
//               {/* Upload */}
//               <div className="mt-1">
//                 <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Image</h5>

//                 {!readOnly ? (
//                   <div
//                     className="border-1 rounded-4 p-4 text-center"
//                     style={{ borderStyle: "dashed", background: "#fafafa" }}
//                     onDragOver={(e) => e.preventDefault()}
//                     onDrop={(e) => {
//                       e.preventDefault();
//                       onAddFiles(Array.from(e.dataTransfer.files || []) as File[]);
//                     }}
//                   >
//                     <div className="mb-2">
//                       <i className="bi bi-upload fs-2" />
//                     </div>
//                     <div className="mb-1">
//                       <span className="fw-semibold">Choose files</span> or drag and drop
//                     </div>
//                     <div className="text-muted small">PNG, JPG up to 10MB each</div>
//                     <div className="text-muted small">You can select multiple images at once</div>
//                     <div className="mt-3">
//                       <label className="btn btn-outline-primary mt-2">
//                         Browse…
//                         <input
//                           type="file"
//                           accept="image/*"
//                           multiple
//                           hidden
//                           onChange={(e) => onAddFiles(Array.from(e.target.files || []) as File[])}
//                         />
//                       </label>
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="text-muted small">Read-only: uploads are disabled.</div>
//                 )}
//               </div>
//               {/* Previews */}
//               {previewFiles.length > 0 && (
//                 <div className="row g-3 mt-3">
//                   {previewFiles.map((item, index) => (
//                     <div key={`preview-${index}`} className="col-6 col-md-4 col-lg-3">
//                       <div className="img-card position-relative">
//                         <button
//                           type="button"
//                           className="btn-close img-x"
//                           aria-label="Remove"
//                           onClick={() => onRemovePreview(index)}
//                         />
//                         <img src={item.url} alt={`preview-${index}`} className="img-thumb" />
//                       </div>

//                       {/* filename under card, single line with … */}
//                       <div className="small text-center mt-1 text-muted text-truncate" title={item.file.name}>
//                         {item.file.name}
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )}

//               {uploadedFiles && uploadedFiles.length > 0 && (
//                 <>
//                   <div className="row g-3 mt-3">
//                     <div className="col-12 mb-1">
//                       <h6 className="text-muted">Already Uploaded Images</h6>
//                     </div>

//                     {uploadedFiles.map((file, index) => (
//                       <div key={`uploaded-${index}`} className="col-6 col-md-4 col-lg-3">
//                         <div className="img-card position-relative">
//                           <button
//                             type="button"
//                             className="btn-close img-x"
//                             aria-label="Remove"
//                             onClick={() => onRemoveUploaded(index)}
//                           />
//                           <img
//                             src={
//                               file.url?.startsWith("http")
//                                 ? file.url
//                                 : `${process.env.REACT_APP_BASE_URL}/${file.url?.replace(
//                                   /^https?:\/\/[^\/]+\/(uploads\/.*)/,
//                                   "$1"
//                                 )}`
//                             }
//                             alt={file.alt_text}
//                             className="img-thumb"
//                           />
//                         </div>
//                         <div className="small text-center mt-1 text-muted text-truncate">
//                           {file.alt_text || `Image ${index + 1}`}
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 </>
//               )}


//               {/* New preview images */}
//               {previewFiles.length === 0 && uploadedFiles.length === 0 && (
//                 <div className="col-12 text-center text-muted">
//                   <i className="bi bi-image fs-2 d-block mb-2" />
//                   No images uploaded yet
//                 </div>
//               )}
//               {/* Pending previews */}
//               {/* {previewFiles?.length > 0 && (
//                 <div className="row mt-3">
//                   {previewFiles.map((item, index) => (
//                     <div key={index} className="col-md-3 position-relative mb-3">
//                       {!readOnly && (
//                         <button
//                           type="button"
//                           className="btn-close position-absolute top-0 end-0 bg-white rounded-circle m-1"
//                           aria-label="Remove"
//                           onClick={() => onRemovePreview(index)}
//                         />
//                       )}
//                       <div className="border p-2 rounded-3">
//                         <img
//                           src={item.url}
//                           alt={`preview-${index}`}
//                           className="img-fluid"
//                           style={{ borderRadius: 8 }}
//                         />
//                         <div className="small text-center mt-1 text-muted">{item.file.name}</div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )} */}

//               {/* Uploaded */}
//               {/* <div className="mt-4">
//                 <div className="mb-2 text-muted">Uploaded</div>
//                 {uploadedFiles?.length === 0 ? (
//                   <div className="text-muted">No images uploaded yet.</div>
//                 ) : (
//                   <div className="row g-3">
//                     {uploadedFiles.map((img, idx) => (
//                       <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={`${img.url}-${idx}`}>
//                         <div className="position-relative border rounded overflow-hidden">
//                           <img
//                             src={fullSrc(img.url)}
//                             alt={img.alt_text}
//                             style={{ width: "100%", height: 120, objectFit: "contain" }}
//                           />
//                           {!readOnly && (
//                             <button
//                               type="button"
//                               className="btn btn-sm btn-danger position-absolute end-0 top-0 m-1"
//                               onClick={() => onRemoveUploaded(idx)}
//                             >
//                               <i className="bi bi-x-lg" />
//                             </button>
//                           )}
//                         </div>
//                         <div className="small mt-1 text-truncate" title={img.alt_text}>
//                           {img.alt_text}
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div> */}
//               <div className="col-12 mb-2" id="row-overlap">
//                 <hr className="" />
//               </div>
//               <div className="d-flex justify-content-between align-items-center mt-4">
//                 <button type="button" onClick={() => setActiveTab("details")} className="btn back-btn surface-btn fw-bold fs-16">
//                   <i className="bi bi-arrow-left me-1" />
//                   Back to Details
//                 </button>
//                 <div>
//                   {/* <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={onBack}>
//                     Cancel
//                   </button> */}
//                   {!hideSaveButton && (
//                     <button type="submit" className="btn btn-primary save-btn surface-btn fw-bold fs-16" disabled={saving || readOnly}>
//                       {saving ? "Saving..." : "Save"}
//                     </button>
//                   )}
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}
//         <style>{`
//         .rc-tabs{display:flex;gap:8px;flex-wrap:wrap}
//         .rc-tab{border:1px solid #e5e7eb;background:#f8f9fb;padding:8px 14px;border-radius:10px;font-weight:600;color:#374151}
//         .rc-tab:hover{background:#eef2ff}

//         .imageuploadui{border:2px dashed #d1d5db;border-radius:14px;padding:40px 12px;text-align:center;cursor:pointer;transition:all .2s}
//         .imageuploadui:hover{border-color:#6d28d9;background:#fbfaff}
//         .uploadedimages{border-radius:10px}
//           /* Thumbnail card so all X icons share the same anchor */
// .img-card{
//   position: relative;
//   border: 1px solid #e5e7eb;
//   border-radius: 12px;
//   background: #fff;
//   height: 180px;                 /* same height for all tiles */
//   padding: 8px;
//   display: flex;
//   align-items: center;
//   justify-content: center;
// }

// /* Image fits nicely without changing card height */
// .img-thumb{
//   max-width: 100%;
//   max-height: 100%;
//   object-fit: contain;
//   border-radius: 10px;
// }

// /* Close button perfectly aligned inside the tile */
// .img-x{
//   position: absolute;
//   top: 0px;
//   right: 0px;
//   z-index: 2;
//   opacity: 0.9;
//   width: 28px;
//   height: 28px;
//   border-radius: 50%;
//   background-color: #fff;
//   box-shadow: 0 1px 3px rgba(0,0,0,.15);
//   padding: 0;
// }

// /* optional: hover feedback */
// .img-card:hover .img-x{ opacity: 1; }

//         /* show/hide desktop vs mobile */
//         .rdt-desktop{display:block}
//         .rdt-mobile{display:none}
//         @media (max-width:1021px){
//           .rdt-desktop{display:none}
//           .rdt-mobile{display:block}
//         }
//       `}</style>
//       </form>
//     </div>
//   );
// };

// export default SharedEditProductUI;
































// // SharedEditProductUI.tsx
// import React, { useState } from "react";
// // import { UIInput, UIReactSelect } from "components/Inputfields";
// import { UIInput, UIReactSelect } from "./WarehouseInputfields";

// import { Table, flexRender } from "@tanstack/react-table";

// type SelectOption = { value: string; label: string };
// type PreviewFile = { file: File; url: string };
// type UploadedImage = { alt_text: string; url: string };

// type MobileCardIds = { idColId: string; titleColId: string; actionColId?: string };

// type Props = {
//     title: string;
//     formik: any;
//     brandOptions: SelectOption[];
//     categoryOptions: SelectOption[];
//     conditionOptions: SelectOption[];
//     statusOptions: SelectOption[];

//     saving?: boolean;
//     onBack: () => void;
//     onSubmit: () => void;

//     // images (container controls the state)
//     previewFiles: PreviewFile[];
//     uploadedFiles: UploadedImage[];
//     onAddFiles: (files: File[]) => void;
//     onRemovePreview: (idx: number) => void;
//     onRemoveUploaded: (idx: number) => void;

//     baseUrl?: string;

//     // Warehouse table (optional): built in parent and passed here
//     warehouseTable?: Table<any>;
//     mobileCardIds?: MobileCardIds;
// };

// // External responsive helpers (adjust paths if needed)
// import { MobileCardsOnly } from "./TableandMobilecomponent";
// // import TablePaginationControls from "components/table/TablePaginationControls";

// const SharedEditProductUI: React.FC<Props> = ({
//     title,
//     formik,
//     brandOptions,
//     categoryOptions,
//     conditionOptions,
//     statusOptions,
//     saving = false,
//     onBack,
//     onSubmit,
//     previewFiles,
//     uploadedFiles,
//     onAddFiles,
//     onRemovePreview,
//     onRemoveUploaded,
//     baseUrl,
//     warehouseTable,
//     mobileCardIds = { idColId: "warehouse_name", titleColId: "warehouse_name", actionColId: "actions" },
// }) => {
//     const [activeTab, setActiveTab] = useState<"details" | "images">("details");

//     const fullSrc = (url: string) =>
//         /^https?:\/\//i.test(url) ? url : `${baseUrl || process.env.REACT_APP_BASE_URL || ""}${url}`;

//     return (
//         <div className="container-xxl py-3">
//             {/* Header */}
//             <div className="d-flex align-items-center justify-content-between mb-3">
//                 <h4 className="mb-0">{title}</h4>
//             </div>

//             {/* Tabs */}
//             <div className="rc-tabs mb-4">
//                 <button
//                     type="button"
//                     className={`rc-tab ${activeTab === "details" ? "rc-tab-active" : ""}`}
//                     onClick={() => setActiveTab("details")}
//                 >
//                     <i className="bi bi-box-seam me-2" />
//                     Product Details
//                 </button>
//                 <button
//                     type="button"
//                     className={`rc-tab ${activeTab === "images" ? "rc-tab-active" : ""}`}
//                     onClick={() => setActiveTab("images")}
//                 >
//                     <i className="bi bi-image me-2" />
//                     Images
//                 </button>
//             </div>

//             <form
//                 onSubmit={(e) => {
//                     e.preventDefault();
//                     onSubmit();
//                 }}
//                 noValidate
//             >
//                 {/* ================= DETAILS ================= */}
//                 {activeTab === "details" && (
//                     <div className="box-shadow-datatables shadow-border-1 p-5">
//                         <div className="card-body">
//                             <div className="row g-4">
//                                 {/* LEFT */}
//                                 <div className="col-lg-6">
//                                     <div className="font-medium header-color-product tracking-wide uppercase mb-4">
//                                         <i className="bi bi-dot pe-1" /> Product Information
//                                     </div>

//                                     <div className="row g-3">
//                                         <div className="col-md-6 mb-3">
//                                             <UIReactSelect
//                                                 formik={formik}
//                                                 name="brand"
//                                                 label="Brand"
//                                                 required
//                                                 placeholder="Select brand"
//                                                 options={brandOptions}
//                                             />
//                                         </div>
//                                         <div className="col-md-6 mb-3">
//                                             <UIReactSelect
//                                                 formik={formik}
//                                                 name="category"
//                                                 label="Category"
//                                                 required
//                                                 placeholder="Select category"
//                                                 options={categoryOptions}
//                                             />
//                                         </div>
//                                     </div>

//                                     <div className="mt-3">
//                                         <label className="form-label fw-semibold">Product Description</label>
//                                         <textarea
//                                             id="description"
//                                             name="description"
//                                             className="form-control"
//                                             rows={4}
//                                             value={formik.values.description}
//                                             onChange={formik.handleChange}
//                                             onBlur={formik.handleBlur}
//                                             style={{
//                                                 borderRadius: 10,
//                                                 background: "#fafafa",
//                                                 borderColor: "#e5e7eb",
//                                                 fontSize: 14,
//                                             }}
//                                             placeholder="Enter product description..."
//                                         />
//                                     </div>
//                                 </div>

//                                 {/* RIGHT */}
//                                 <div className="col-lg-6">
//                                     <div className="font-medium header-color-product tracking-wide uppercase mb-4">
//                                         <i className="bi bi-dot pe-1" /> Product Details
//                                     </div>

//                                     <UIInput
//                                         formik={formik}
//                                         name="name"
//                                         label="Product Name"
//                                         required
//                                         placeholder="Enter product name"
//                                     />
//                                     <UIInput
//                                         formik={formik}
//                                         name="sku"
//                                         label="SKU"
//                                         required
//                                         placeholder="Enter SKU"
//                                     />

//                                     <div className="row g-3">
//                                         <div className="col-md-6 mb-3">
//                                             <UIInput formik={formik} name="qty" label="Quantity" required placeholder="0" />
//                                         </div>
//                                         <div className="col-md-6 mb-3">
//                                             <UIInput formik={formik} name="threshold_qty" label="Threshold Quantity" placeholder="0" />
//                                         </div>
//                                     </div>

//                                     <div className="mt-3">
//                                         <div className="fw-semibold mb-2">Dimensions/Measurements (inches)</div>
//                                         <div className="row g-3">
//                                             <div className="col-md-4">
//                                                 <UIInput formik={formik} name="length" label="Length" placeholder="0.0" />
//                                             </div>
//                                             <div className="col-md-4">
//                                                 <UIInput formik={formik} name="height" label="Height" placeholder="0.0" />
//                                             </div>
//                                             <div className="col-md-4">
//                                                 <UIInput formik={formik} name="width" label="Width" placeholder="0.0" />
//                                             </div>
//                                             <div className="col-md-12">
//                                                 <UIInput formik={formik} name="weight" label="Weight (gms)" placeholder="0.0" />
//                                             </div>
//                                         </div>
//                                     </div>

//                                     <div className="row g-3 mt-1">
//                                         <div className="col-md-6 mb-3">
//                                             <UIReactSelect
//                                                 formik={formik}
//                                                 name="item_condition"
//                                                 label="Item Condition"
//                                                 placeholder="Select condition"
//                                                 options={conditionOptions}
//                                             />
//                                         </div>
//                                         <div className="col-md-6 mb-3">
//                                             <UIReactSelect
//                                                 formik={formik}
//                                                 name="statuss"
//                                                 label="Status"
//                                                 required
//                                                 options={statusOptions}
//                                                 placeholder="Select status"
//                                             />
//                                         </div>
//                                     </div>
//                                 </div>
//                             </div>

//                             {/* --------------- Warehouse / Locations (responsive) --------------- */}
//                             {!!warehouseTable && (
//                                 <div className="mt-5">
//                                     <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Inventory & Locations</h5>

//                                     {/* DESKTOP/TABLET */}
//                                     <div className="rdt-desktop">
//                                         <div className="table-responsive">
//                                             <table className="table table-hover align-middle">
//                                                 <thead>
//                                                     {warehouseTable.getHeaderGroups().map((hg) => (
//                                                         <tr key={hg.id}>
//                                                             {hg.headers.map((header) => (
//                                                                 <th key={header.id} style={{ whiteSpace: "nowrap" }}>
//                                                                     {header.isPlaceholder
//                                                                         ? null
//                                                                         : flexRender(header.column.columnDef.header, header.getContext())}
//                                                                 </th>
//                                                             ))}
//                                                         </tr>
//                                                     ))}
//                                                 </thead>
//                                                 <tbody>
//                                                     {warehouseTable.getRowModel().rows.map((row) => (
//                                                         <tr key={row.id}>
//                                                             {row.getVisibleCells().map((cell) => (
//                                                                 <td key={cell.id} style={{ verticalAlign: "middle" }}>
//                                                                     {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                                                                 </td>

//                                                             ))}
//                                                         </tr>
//                                                     ))}
//                                                 </tbody>
//                                             </table>
//                                         </div>
//                                     </div>

//                                     {/* MOBILE (≤ 1021px) */}
//                                     <div className="rdt-mobile">
//                                         <MobileCardsOnly
//                                             table={warehouseTable as any}
//                                             idColId={mobileCardIds.idColId}
//                                             titleColId={mobileCardIds.titleColId}
//                                             actionColId={mobileCardIds.actionColId || "actions"}
//                                         />
//                                     </div>

//                                     {/* Empty / Pagination */}
//                                     {warehouseTable.getRowModel().rows.length === 0 ? (
//                                         <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
//                                             No records found
//                                         </div>
//                                     ) : (
//                                         <div className="mt-4 mb-4">
//                                             {/* <TablePaginationControls table={warehouseTable as any} /> */}
//                                         </div>
//                                     )}
//                                 </div>
//                             )}

//                             {/* Footer */}
//                             <div className="d-flex justify-content-between align-items-center mt-4">
//                                 <button type="button" onClick={onBack} className="btn btn-light fw-bold">
//                                     <i className="bi bi-chevron-left me-2" /> Back
//                                 </button>
//                                 <button type="submit" className="btn btn-primary fw-bold" disabled={saving}>
//                                     {saving ? "Saving..." : "Save"}
//                                 </button>
//                             </div>
//                         </div>
//                     </div>
//                 )}

//                 {/* ================= IMAGES ================= */}
//                 {activeTab === "images" && (
//                     <div className="box-shadow-datatables shadow-border-1 p-5">
//                         <div className="card-body">
//                             {/* Upload */}
//                             <div className="mt-1">
//                                 <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Image</h5>
//                                 <div
//                                     className="border-1 rounded-4 p-4 text-center"
//                                     style={{ borderStyle: "dashed", background: "#fafafa" }}
//                                     onDragOver={(e) => e.preventDefault()}
//                                     onDrop={(e) => {
//                                         e.preventDefault();
//                                         onAddFiles(Array.from(e.dataTransfer.files || []) as File[]);
//                                     }}
//                                 >
//                                     <div className="mb-2">
//                                         <i className="bi bi-upload fs-2" />
//                                     </div>
//                                     <div className="mb-1">
//                                         <span className="fw-semibold">Choose files</span> or drag and drop
//                                     </div>
//                                     <div className="text-muted small">PNG, JPG up to 10MB each</div>
//                                     <div className="text-muted small">You can select multiple images at once</div>
//                                     <div className="mt-3">
//                                         <label className="btn btn-outline-primary mt-2">
//                                             Browse…
//                                             <input
//                                                 type="file"
//                                                 accept="image/*"
//                                                 multiple
//                                                 hidden
//                                                 onChange={(e) =>
//                                                     onAddFiles(Array.from(e.target.files || []) as File[])
//                                                 }
//                                             />
//                                         </label>
//                                     </div>
//                                 </div>
//                             </div>

//                             {/* Pending previews */}
//                             {previewFiles?.length > 0 && (
//                                 <div className="row mt-3">
//                                     {previewFiles.map((item, index) => (
//                                         <div key={index} className="col-md-3 position-relative mb-3">
//                                             <button
//                                                 type="button"
//                                                 className="btn-close position-absolute top-0 end-0 bg-white rounded-circle m-1"
//                                                 aria-label="Remove"
//                                                 onClick={() => onRemovePreview(index)}
//                                             />
//                                             <div className="border p-2 rounded-3">
//                                                 <img
//                                                     src={item.url}
//                                                     alt={`preview-${index}`}
//                                                     className="img-fluid"
//                                                     style={{ borderRadius: 8 }}
//                                                 />
//                                                 <div className="small text-center mt-1 text-muted">
//                                                     {item.file.name}
//                                                 </div>
//                                             </div>
//                                         </div>
//                                     ))}
//                                 </div>
//                             )}

//                             {/* Uploaded */}
//                             <div className="mt-4">
//                                 <div className="mb-2 text-muted">Uploaded</div>
//                                 {uploadedFiles?.length === 0 ? (
//                                     <div className="text-muted">No images uploaded yet.</div>
//                                 ) : (
//                                     <div className="row g-3">
//                                         {uploadedFiles.map((img, idx) => (
//                                             <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={`${img.url}-${idx}`}>
//                                                 <div className="position-relative border rounded overflow-hidden">
//                                                     <img
//                                                         src={fullSrc(img.url)}
//                                                         alt={img.alt_text}
//                                                         style={{ width: "100%", height: 120, objectFit: "cover" }}
//                                                     />
//                                                     <button
//                                                         type="button"
//                                                         className="btn btn-sm btn-danger position-absolute end-0 top-0 m-1"
//                                                         onClick={() => onRemoveUploaded(idx)}
//                                                     >
//                                                         <i className="bi bi-x-lg" />
//                                                     </button>
//                                                 </div>
//                                                 <div className="small mt-1 text-truncate" title={img.alt_text}>
//                                                     {img.alt_text}
//                                                 </div>
//                                             </div>
//                                         ))}
//                                     </div>
//                                 )}
//                             </div>

//                             {/* Footer */}
//                             <div className="d-flex justify-content-between align-items-center mt-4">
//                                 <button
//                                     type="button"
//                                     onClick={() => setActiveTab("details")}
//                                     className="btn btn-light"
//                                 >
//                                     <i className="bi bi-arrow-left me-1" />
//                                     Back to Details
//                                 </button>
//                                 <div>
//                                     <button type="button" className="btn btn-outline-secondary me-2" onClick={onBack}>
//                                         Cancel
//                                     </button>
//                                     <button type="submit" className="btn btn-primary" disabled={saving}>
//                                         {saving ? "Saving..." : "Save"}
//                                     </button>
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 )}
//             </form>
//         </div>
//     );
// };

// export default SharedEditProductUI;
